import { renderMarkdown, initializeCopyButtons } from '../utils/markdown';
export class UIManager {
    constructor(adapter) {
        this.shadowHost = null;
        this.shadowRoot = null;
        this.floatingIcon = null;
        this.chatPanel = null;
        this.panelOpen = false;
        this.isWideMode = false;
        this.isLeftMode = false;
        this.currentChannelId = null;
        this.channels = [];
        this.messages = [];
        this.markdownLinkInterceptorInitialized = false;
        // Platform selector removed — platform is determined by chatContext
        this.selectedChatContext = 'cms';
        this.version = '';
        this.modelTiers = [];
        this.selectedModel = 'default';
        // Analytics overlay
        this.analyticsContainer = null;
        this.analyticsUnmount = null;
        this.analyticsScriptLoaded = false;
        this.assetsBaseUrl = '';
        // Analytics inline (injected into host page)
        this.analyticsInlineUnmount = null;
        this.analyticsInlineHost = null;
        this.analyticsThemeObserver = null;
        this.analyticsMountPoint = null;
        this.analyticsInlineMounting = null;
        this.creditBalance = null;
        this.creditCountdownTimer = null;
        this.currentView = 'chat';
        // MCP Bridge state
        this.mcpStatus = 'disconnected';
        this.mcpActivities = [];
        this.onMCPConnect = null;
        this.onMCPDisconnect = null;
        this.mcpErrorMessage = null;
        // Execution plan state
        this.currentPlanElement = null;
        this.currentPlanSteps = [];
        // API Keys state
        this.apiKeys = [];
        this.apiKeysLoaded = false;
        this.newlyCreatedKey = null;
        this.adapter = adapter;
        // Restore saved position preference
        const savedPosition = localStorage.getItem(UIManager.PANEL_POSITION_KEY);
        if (savedPosition === 'left') {
            this.isLeftMode = true;
        }
        // Restore saved model preference
        const savedModel = localStorage.getItem(UIManager.MODEL_KEY);
        if (savedModel) {
            this.selectedModel = savedModel;
        }
    }
    /**
     * Initialize Shadow DOM container for style isolation.
     * All SDK UI elements live inside this shadow root so host page CSS cannot affect them.
     */
    initShadowDOM() {
        if (this.shadowRoot)
            return;
        this.shadowHost = document.createElement('div');
        this.shadowHost.id = 'balancy-ai-root';
        // The host element itself must not be visible or take layout space
        this.shadowHost.style.cssText =
            'position: fixed; top: 0; left: 0; width: 0; height: 0; overflow: visible; z-index: 2147483646; pointer-events: none;';
        document.body.appendChild(this.shadowHost);
        this.shadowRoot = this.shadowHost.attachShadow({ mode: 'open' });
        // Expose UIManager on shadow root for debugging (e.g., showPlanPreview)
        this.shadowRoot.__uiManager = this;
        // Load CSS inside shadow root
        this.loadCSS();
    }
    createFloatingIcon() {
        // Ensure shadow DOM is ready
        this.initShadowDOM();
        if (this.floatingIcon && this.shadowRoot?.contains(this.floatingIcon)) {
            return;
        }
        this.floatingIcon = document.createElement('button');
        this.floatingIcon.className = 'balancy-floating-icon';
        this.floatingIcon.setAttribute('aria-label', 'Open AI-Assistant');
        const iconUrl = this.adapter.getAssetURL('icons/floating-button.png');
        Object.assign(this.floatingIcon.style, {
            position: 'fixed',
            bottom: '24px',
            right: this.isLeftMode ? 'auto' : '24px',
            left: this.isLeftMode ? '24px' : 'auto',
            width: '56px',
            height: '56px',
            borderRadius: '14px',
            background: 'white',
            border: '2px solid #e0e0e0',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.1)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            zIndex: '999999',
            padding: '0',
            overflow: 'hidden',
            pointerEvents: 'auto',
        });
        this.floatingIcon.innerHTML = `
      <img
        class="balancy-icon-img"
        src="${iconUrl}"
        alt="AI-Assistant"
        style="width: 100%; height: 100%; object-fit: cover; border-radius: 12px;"
      >
      <span class="balancy-mcp-dot" id="balancy-mcp-dot" style="display:none"></span>
    `;
        this.floatingIcon.addEventListener('mouseenter', () => {
            if (this.floatingIcon) {
                this.floatingIcon.style.transform = 'scale(1.1)';
                this.floatingIcon.style.boxShadow =
                    '0 6px 16px rgba(0, 0, 0, 0.2), 0 3px 6px rgba(0, 0, 0, 0.15)';
            }
        });
        this.floatingIcon.addEventListener('mouseleave', () => {
            if (this.floatingIcon) {
                this.floatingIcon.style.transform = 'scale(1)';
                this.floatingIcon.style.boxShadow =
                    '0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.1)';
            }
        });
        this.floatingIcon.addEventListener('click', () => this.togglePanel());
        this.shadowRoot.appendChild(this.floatingIcon);
        console.log('[UIManager] Floating icon created (inside Shadow DOM)');
    }
    async createChatPanel(user = null) {
        // Ensure shadow DOM is ready
        this.initShadowDOM();
        if (this.chatPanel && this.shadowRoot?.contains(this.chatPanel)) {
            return;
        }
        const iconUrl = this.adapter.getAssetURL('icons/floating-button.png');
        // Load channels and model tiers if user is authenticated
        if (user) {
            await Promise.all([this.loadChannels(user), this.loadModelTiers()]);
        }
        this.chatPanel = document.createElement('div');
        this.chatPanel.className = 'balancy-chat-panel';
        // Enable pointer events on the panel (host has pointer-events: none)
        this.chatPanel.style.pointerEvents = 'auto';
        if (user) {
            this.chatPanel.innerHTML = this.getAuthenticatedPanelHTML(iconUrl, user);
        }
        else {
            this.chatPanel.innerHTML = this.getUnauthenticatedPanelHTML(iconUrl);
        }
        this.shadowRoot.appendChild(this.chatPanel);
        this.setupPanelEventListeners();
        // Load width preference from localStorage
        const savedWidth = localStorage.getItem(UIManager.PANEL_WIDTH_KEY);
        if (savedWidth === 'wide') {
            this.isWideMode = true;
            this.chatPanel.classList.add('wide-mode');
        }
        // Load position preference from localStorage
        if (this.isLeftMode) {
            this.chatPanel.classList.add('left-mode');
        }
        // Initialize channel if user is authenticated
        if (user && this.channels.length > 0) {
            await this.initializeChannel();
        }
        // Show welcome message if no messages loaded (new user or empty channel)
        if (user && this.messages.length === 0) {
            this.showWelcomeMessage();
        }
        // Load credit balance if user is authenticated
        if (user) {
            this.loadCreditBalance();
        }
        // Restore saved chat context and update border
        const savedContext = localStorage.getItem(UIManager.CONTEXT_KEY);
        if (savedContext === 'cms' ||
            savedContext === 'sdk-unity' ||
            savedContext === 'sdk-ts') {
            this.selectedChatContext = savedContext;
        }
        this.updateContextBorder();
        console.log('[UIManager] Chat panel created');
    }
    loadCSS() {
        if (!this.shadowRoot)
            return;
        // Check if CSS is already loaded inside shadow root
        if (this.shadowRoot.querySelector('#balancy-sdk-styles')) {
            return;
        }
        const link = document.createElement('link');
        link.id = 'balancy-sdk-styles';
        link.rel = 'stylesheet';
        link.href = this.adapter.getAssetURL('content.css');
        this.shadowRoot.appendChild(link);
    }
    async loadModelTiers() {
        try {
            const headers = this.adapter.getAuthHeaders();
            const response = await fetch(`${this.adapter.apiBaseUrl}/api/chat/models`, { headers });
            if (response.ok) {
                const data = await response.json();
                if (data.success && data.data?.models) {
                    this.modelTiers = data.data.models;
                    if (data.data.defaultModel &&
                        !localStorage.getItem(UIManager.MODEL_KEY)) {
                        this.selectedModel = data.data.defaultModel;
                    }
                    console.log(`[UIManager] Loaded ${this.modelTiers.length} model tiers`);
                }
            }
        }
        catch (error) {
            console.warn('[UIManager] Failed to load model tiers:', error);
        }
    }
    async loadChannels(_user) {
        try {
            const headers = this.adapter.getAuthHeaders();
            const response = await fetch(`${this.adapter.apiBaseUrl}/api/chat/channels`, {
                headers,
            });
            if (response.ok) {
                const data = await response.json();
                this.channels = data.data.channels.map((ch) => ({
                    id: ch.id,
                    name: ch.name,
                    mode: ch.mode,
                    model: ch.model || 'gpt-4.1-mini',
                    chatContext: (ch.chatContext || 'cms'),
                    messageCount: ch.messageCount,
                    totalTokens: ch.totalTokens,
                    creditsUsed: ch.creditsUsed || 0,
                    lastMessageAt: ch.lastMessageAt,
                    createdAt: ch.createdAt,
                    updatedAt: ch.updatedAt,
                    contextOptimized: true, // always on
                    cachedItems: ch.cachedItems || [],
                }));
                console.log(`[UIManager] Loaded ${this.channels.length} channels`);
            }
            else {
                console.warn(`[UIManager] Failed to load channels: ${response.status}`);
                this.channels = [];
            }
        }
        catch (error) {
            console.error('[UIManager] Failed to load channels:', error);
            this.channels = [];
        }
    }
    async loadChannelMessages(channelId) {
        try {
            const headers = this.adapter.getAuthHeaders();
            const response = await fetch(`${this.adapter.apiBaseUrl}/api/chat/channels/${channelId}/messages`, {
                headers,
            });
            if (response.ok) {
                const data = await response.json();
                this.messages = data.data.messages.map((msg) => ({
                    id: msg.id,
                    role: msg.role,
                    content: msg.content,
                    timestamp: msg.createdAt,
                    tokens: {
                        total: msg.totalTokens,
                        prompt: msg.promptTokens,
                        completion: msg.completionTokens,
                    },
                }));
                this.renderMessages();
                console.log(`[UIManager] Loaded ${this.messages.length} messages for channel ${channelId}`);
            }
            else {
                console.warn(`[UIManager] Failed to load messages for channel ${channelId}: ${response.status}`);
                this.messages = [];
            }
        }
        catch (error) {
            console.error('[UIManager] Failed to load messages:', error);
            this.messages = [];
        }
    }
    async initializeChannel() {
        const channelSelect = this.chatPanel?.querySelector('#balancy-channel-select');
        if (!channelSelect || this.channels.length === 0) {
            return;
        }
        // Try to load last channel from localStorage
        const lastChannelId = localStorage.getItem(UIManager.LAST_CHANNEL_KEY);
        let channelToLoad = null;
        if (lastChannelId && this.channels.some((ch) => ch.id === lastChannelId)) {
            // Last channel exists in current channels
            channelToLoad = lastChannelId;
            channelSelect.value = lastChannelId;
        }
        else {
            // Use first channel as default
            channelToLoad = this.channels[0].id;
            channelSelect.value = channelToLoad;
        }
        if (channelToLoad) {
            this.currentChannelId = channelToLoad;
            // Update adapter's current channel
            if ('setCurrentChannel' in this.adapter) {
                this.adapter.setCurrentChannel(channelToLoad);
            }
            // Restore context cache and optimization flag for this channel
            const channel = this.channels.find((ch) => ch.id === channelToLoad);
            if (channel) {
                this.syncChannelCacheState(channel);
            }
            // Load messages for the channel
            await this.loadChannelMessages(channelToLoad);
            this.updateUsageIndicator();
            console.log(`[UIManager] Initialized with channel: ${channelToLoad}`);
        }
    }
    /**
     * Sync cache state from a channel's persisted data to the adapter.
     * Sets contextOptimized flag and restores cached items.
     */
    syncChannelCacheState(channel) {
        if (!('setContextOptimized' in this.adapter))
            return;
        const adapter = this.adapter;
        // Set the optimization flag on the adapter
        adapter.setContextOptimized(channel.contextOptimized);
        // Restore cache from persisted keys (async, runs in background)
        if (channel.contextOptimized && channel.cachedItems.length > 0) {
            adapter.restoreCacheFromKeys(channel.cachedItems).catch((err) => {
                console.warn('[UIManager] Failed to restore cache:', err);
            });
        }
        else if ('getContextCache' in adapter) {
            // Clear cache if optimization is off or no items
            const cache = adapter.getContextCache();
            if (cache)
                cache.clear();
        }
    }
    /**
     * Handle the optimization toggle change.
     * PATCHes the channel on the backend and updates local state.
     */
    async onToggleOptimization(isOn) {
        if (!this.currentChannelId)
            return;
        // Update local channel data
        const channel = this.channels.find((ch) => ch.id === this.currentChannelId);
        if (channel) {
            channel.contextOptimized = isOn;
        }
        // Update adapter state
        if ('setContextOptimized' in this.adapter) {
            this.adapter.setContextOptimized(isOn);
        }
        // Clear cache if turning off
        if (!isOn && 'getContextCache' in this.adapter) {
            const cache = this.adapter.getContextCache();
            if (cache)
                cache.clear();
        }
        // Persist to backend
        try {
            const headers = this.adapter.getAuthHeaders();
            await fetch(`${this.adapter.apiBaseUrl}/api/chat/channels/${this.currentChannelId}`, {
                method: 'PATCH',
                headers,
                body: JSON.stringify({ contextOptimized: isOn }),
            });
            console.log(`[UIManager] Context optimization ${isOn ? 'enabled' : 'disabled'} for channel ${this.currentChannelId}`);
        }
        catch (error) {
            console.error('[UIManager] Failed to toggle optimization:', error);
        }
    }
    /**
     * Update the optimization toggle UI to reflect the current channel's state.
     */
    updateOptimizationToggle() {
        const channel = this.channels.find((ch) => ch.id === this.currentChannelId);
        const toggle = this.chatPanel?.querySelector('#balancy-optimization-toggle');
        if (toggle && channel) {
            toggle.checked = channel.contextOptimized;
        }
    }
    renderMessages() {
        const messagesContainer = this.chatPanel?.querySelector('#balancy-messages');
        if (!messagesContainer)
            return;
        messagesContainer.innerHTML = '';
        // Show welcome message if no messages
        if (this.messages.length === 0) {
            this.showWelcomeMessage();
            return;
        }
        this.messages.forEach((message) => {
            const messageDiv = document.createElement('div');
            messageDiv.className = `balancy-message ${message.role === 'assistant' ? 'bot' : 'user'}`;
            // Add message ID as data attribute if available
            if (message.id) {
                messageDiv.setAttribute('data-message-id', message.id);
            }
            const timestamp = this.formatTimestamp(message.timestamp);
            const tokensHTML = message.tokens?.total
                ? `<span class="balancy-message-tokens">${this.formatTokens(message.tokens.total)} tokens</span>`
                : '';
            // Render markdown for assistant messages, escape HTML for user messages
            const messageContent = message.role === 'assistant'
                ? renderMarkdown(message.content)
                : this.escapeHTML(message.content);
            // Add feedback buttons for assistant messages
            const feedbackHTML = message.role === 'assistant' && message.id
                ? this.createFeedbackHTML(message.id)
                : '';
            messageDiv.innerHTML = `
        <div class="balancy-message-content">
          <div class="balancy-message-text">${messageContent}</div>
          <div class="balancy-message-footer">
            <div class="balancy-message-timestamp">${timestamp}</div>
            ${tokensHTML}
          </div>
          ${feedbackHTML}
        </div>
      `;
            messagesContainer.appendChild(messageDiv);
        });
        // Initialize copy buttons for code blocks
        initializeCopyButtons(messagesContainer);
        // Initialize markdown link interceptor (only once on chat panel)
        this.initializeMarkdownLinkInterceptor();
        // Initialize feedback handlers
        this.initializeFeedbackHandlers(messagesContainer);
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
    }
    formatTimestamp(timestamp) {
        const date = new Date(timestamp);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        if (diffMins < 1)
            return 'Just now';
        if (diffMins < 60)
            return `${diffMins}m ago`;
        const diffHours = Math.floor(diffMins / 60);
        if (diffHours < 24)
            return `${diffHours}h ago`;
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    formatTokens(tokens) {
        if (tokens >= 1000) {
            return `${(tokens / 1000).toFixed(1)}k`;
        }
        return tokens.toString();
    }
    escapeHTML(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }
    /**
     * Show welcome message with starter question buttons
     * Displayed when chat is empty (new channel or no messages)
     */
    showWelcomeMessage() {
        const messagesContainer = this.chatPanel?.querySelector('#balancy-messages');
        if (!messagesContainer)
            return;
        // Don't show if there are already messages
        if (this.messages.length > 0)
            return;
        // Don't show if welcome message already exists
        if (messagesContainer.querySelector('.balancy-welcome-message'))
            return;
        const welcomeDiv = document.createElement('div');
        welcomeDiv.className = 'balancy-welcome-message';
        const ctx = this.getActiveContext();
        const questions = UIManager.STARTER_QUESTIONS[ctx];
        const subtitle = UIManager.WELCOME_SUBTITLES[ctx];
        const starterButtons = questions
            .map((q) => `<button class="balancy-starter-btn">${this.escapeHTML(q)}</button>`)
            .join('');
        welcomeDiv.innerHTML = `
      <div class="balancy-welcome-avatar">
        <img src="${this.adapter.getAssetURL('icons/floating-button.png')}" alt="Nia" class="balancy-welcome-avatar-img" />
      </div>
      <div class="balancy-welcome-text">
        <div class="balancy-welcome-greeting">Hi! I'm <strong>Nia</strong>, your Balancy onboarding assistant.</div>
        <div class="balancy-welcome-subtitle">${subtitle}</div>
      </div>
      <div class="balancy-starter-buttons">
        ${starterButtons}
      </div>
    `;
        // Add click handlers for starter buttons
        welcomeDiv.querySelectorAll('.balancy-starter-btn').forEach((btn) => {
            btn.addEventListener('click', () => {
                const question = btn.textContent || '';
                const input = this.chatPanel?.querySelector('#balancy-input');
                if (input) {
                    input.value = question;
                }
                this.onSendMessage();
            });
        });
        messagesContainer.appendChild(welcomeDiv);
    }
    /**
     * Remove welcome message from the chat
     */
    hideWelcomeMessage() {
        const welcomeDiv = this.chatPanel?.querySelector('.balancy-welcome-message');
        if (welcomeDiv) {
            welcomeDiv.classList.add('balancy-welcome-hiding');
            // Remove after animation
            setTimeout(() => welcomeDiv.remove(), 300);
        }
    }
    /**
     * Render follow-up suggestion chips below a message element
     */
    renderSuggestionChips(messageDiv, suggestions) {
        if (!suggestions || suggestions.length === 0)
            return;
        const chipsContainer = document.createElement('div');
        chipsContainer.className = 'balancy-suggestion-chips';
        suggestions.forEach((suggestion) => {
            const chip = document.createElement('button');
            chip.className = 'balancy-suggestion-chip';
            chip.textContent = suggestion;
            chip.addEventListener('click', () => {
                // Remove all suggestion chip containers when one is clicked
                this.chatPanel
                    ?.querySelectorAll('.balancy-suggestion-chips')
                    .forEach((c) => c.remove());
                // Set input and send
                const input = this.chatPanel?.querySelector('#balancy-input');
                if (input) {
                    input.value = suggestion;
                }
                this.onSendMessage();
            });
            chipsContainer.appendChild(chip);
        });
        // Insert after the message element
        messageDiv.after(chipsContainer);
        // Scroll to show chips
        const messagesContainer = this.chatPanel?.querySelector('#balancy-messages');
        if (messagesContainer) {
            messagesContainer.scrollTop = messagesContainer.scrollHeight;
        }
    }
    formatRelativeTime(timestamp) {
        if (!timestamp)
            return '';
        const date = new Date(timestamp);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        if (diffMins < 1)
            return 'just now';
        if (diffMins < 60)
            return `${diffMins}m ago`;
        const diffHours = Math.floor(diffMins / 60);
        if (diffHours < 24)
            return `${diffHours}h ago`;
        const diffDays = Math.floor(diffHours / 24);
        if (diffDays === 1)
            return 'yesterday';
        if (diffDays < 7)
            return `${diffDays}d ago`;
        return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
    createUsageProgress(tokens, maxTokens = 100000) {
        const percentage = Math.min((tokens / maxTokens) * 100, 100);
        const radius = 6;
        const circumference = 2 * Math.PI * radius;
        const offset = circumference - (percentage / 100) * circumference;
        // Color based on usage: green -> yellow -> red
        let color = '#10b981'; // green
        if (percentage > 70)
            color = '#f59e0b'; // yellow
        if (percentage > 90)
            color = '#ef4444'; // red
        return `
      <svg class="balancy-usage-circle" width="16" height="16" viewBox="0 0 16 16">
        <circle
          class="balancy-usage-circle-bg"
          cx="8" cy="8" r="${radius}"
          fill="none"
          stroke="rgba(255,255,255,0.1)"
          stroke-width="4"
        />
        <circle
          class="balancy-usage-circle-progress"
          cx="8" cy="8" r="${radius}"
          fill="none"
          stroke="${color}"
          stroke-width="4"
          stroke-dasharray="${circumference}"
          stroke-dashoffset="${offset}"
          stroke-linecap="round"
          transform="rotate(-90 8 8)"
        />
      </svg>
    `;
    }
    /**
     * Initialize markdown link interceptor to handle .md file clicks
     * This is initialized once on the chat panel to use event delegation
     */
    initializeMarkdownLinkInterceptor() {
        // Only initialize once
        if (this.markdownLinkInterceptorInitialized) {
            return;
        }
        const chatArea = this.chatPanel?.querySelector('#balancy-chat-area');
        if (!chatArea) {
            console.warn('[UIManager] Cannot initialize markdown link interceptor: chat area not found');
            return;
        }
        chatArea.addEventListener('click', async (e) => {
            const target = e.target;
            // Check if clicked element is a link
            if (target.tagName === 'A' || target.closest('a')) {
                const link = target.tagName === 'A'
                    ? target
                    : target.closest('a');
                const href = link.getAttribute('href') || '';
                // Check if it's a markdown file link
                if (href.endsWith('.md')) {
                    e.preventDefault();
                    e.stopPropagation();
                    // Find parent document to resolve relative paths
                    const parentDoc = link.closest('.balancy-document');
                    const parentPath = parentDoc?.getAttribute('data-doc-path') || '';
                    // Resolve the path (handle relative links)
                    const resolvedPath = this.resolveDocPath(href, parentPath);
                    // Debug logging
                    console.log('[DEBUG] Link:', href, '| Parent:', parentPath, '| Resolved:', resolvedPath);
                    // Show loading indicator
                    const typingIndicator = this.chatPanel?.querySelector('#balancy-typing-indicator');
                    typingIndicator?.classList.add('show');
                    try {
                        await this.fetchAndDisplayDocument(resolvedPath);
                    }
                    catch (error) {
                        console.error('[UIManager] Failed to fetch document:', error);
                        this.addDocumentMessage(resolvedPath, 'Failed to load document. Please try again.', true);
                    }
                    finally {
                        typingIndicator?.classList.remove('show');
                    }
                }
            }
        });
        this.markdownLinkInterceptorInitialized = true;
        console.log('[UIManager] Markdown link interceptor initialized once');
    }
    /**
     * Resolve relative document paths
     */
    resolveDocPath(href, parentPath) {
        // Strip doc:// protocol prefix (used by RAG citations)
        if (href.startsWith('doc://')) {
            return href.substring(6); // Remove "doc://" prefix - already an absolute doc path
        }
        // If href starts with /, it's absolute
        if (href.startsWith('/')) {
            return href.substring(1); // Remove leading slash
        }
        // Build the full path (either from parent or root)
        let fullPath;
        if (parentPath) {
            // Get the directory of the parent document
            const parentDir = parentPath.includes('/')
                ? parentPath.substring(0, parentPath.lastIndexOf('/'))
                : '';
            fullPath = parentDir ? `${parentDir}/${href}` : href;
        }
        else {
            fullPath = href;
        }
        // Normalize the path by resolving .. and . segments
        const segments = fullPath.split('/');
        const resolved = [];
        for (const segment of segments) {
            if (segment === '..') {
                // Go up one directory
                if (resolved.length > 0) {
                    resolved.pop();
                }
            }
            else if (segment !== '.' && segment !== '') {
                // Add normal segments (skip . and empty segments)
                resolved.push(segment);
            }
        }
        return resolved.join('/');
    }
    /**
     * Fetch and display documentation content
     */
    async fetchAndDisplayDocument(docPath) {
        const apiBaseUrl = this.adapter.apiBaseUrl;
        const response = await fetch(`${apiBaseUrl}/api/chat/docs/content/${docPath}`);
        if (!response.ok) {
            throw new Error(`Failed to fetch document: ${response.status}`);
        }
        const data = await response.json();
        if (data.success && data.data.content) {
            this.addDocumentMessage(docPath, data.data.content);
        }
        else {
            throw new Error(data.error?.message || 'Failed to load document');
        }
    }
    /**
     * Add a collapsible document message to the chat
     */
    addDocumentMessage(docPath, content, isError = false) {
        const messagesContainer = this.chatPanel?.querySelector('#balancy-messages');
        if (!messagesContainer)
            return;
        const docDiv = document.createElement('div');
        docDiv.className = 'balancy-message balancy-document-message';
        const docId = `doc-${Date.now()}`;
        const docTitle = docPath.split('/').pop() || docPath;
        if (isError) {
            docDiv.innerHTML = `
        <div class="balancy-document-error">
          <div class="balancy-document-header">
            <span class="balancy-document-icon">📄</span>
            <span class="balancy-document-title">${this.escapeHTML(docTitle)}</span>
          </div>
          <div class="balancy-document-error-message">${this.escapeHTML(content)}</div>
        </div>
      `;
        }
        else {
            const renderedContent = renderMarkdown(content);
            docDiv.innerHTML = `
        <div class="balancy-document" data-doc-path="${this.escapeHTML(docPath)}">
          <div class="balancy-document-header" id="${docId}-header">
            <span class="balancy-document-icon">📄</span>
            <span class="balancy-document-title">${this.escapeHTML(docTitle)}</span>
            <button class="balancy-document-toggle" data-doc-id="${docId}">
              <span class="balancy-document-toggle-icon">▼</span>
            </button>
          </div>
          <div class="balancy-document-content" id="${docId}-content">
            ${renderedContent}
          </div>
        </div>
      `;
            // Add toggle functionality - entire header is clickable
            const docHeader = docDiv.querySelector('.balancy-document-header');
            const docContent = docDiv.querySelector('.balancy-document-content');
            const toggleIcon = docDiv.querySelector('.balancy-document-toggle-icon');
            docHeader?.addEventListener('click', () => {
                const isCollapsed = docContent.style.display === 'none';
                docContent.style.display = isCollapsed ? 'block' : 'none';
                if (toggleIcon) {
                    toggleIcon.textContent = isCollapsed ? '▼' : '▶';
                }
            });
        }
        messagesContainer.appendChild(docDiv);
        // Initialize copy buttons for the new content
        // Note: Link interceptor is initialized once on the chat panel, not per-document
        if (!isError) {
            initializeCopyButtons(docDiv);
        }
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
        console.log(`[UIManager] Document displayed: ${docPath}`);
    }
    // ============================================================
    // FEEDBACK METHODS
    // ============================================================
    /**
     * Create feedback HTML for a message
     */
    createFeedbackHTML(messageId) {
        return `
      <div class="balancy-message-feedback" data-message-id="${this.escapeHTML(messageId)}">
        <div class="balancy-feedback-buttons">
          <button class="balancy-feedback-button balancy-feedback-positive" data-feedback-type="positive" title="Like this response">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M14 9V5a3 3 0 00-3-3l-4 9v11h11.28a2 2 0 002-1.7l1.38-9a2 2 0 00-2-2.3zM7 22H4a2 2 0 01-2-2v-7a2 2 0 012-2h3" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <button class="balancy-feedback-button balancy-feedback-negative" data-feedback-type="negative" title="Dislike this response">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M10 15v4a3 3 0 003 3l4-9V2H5.72a2 2 0 00-2 1.7l-1.38 9a2 2 0 002 2.3zm7-13h2.67A2.31 2.31 0 0122 4v7a2.31 2.31 0 01-2.33 2H17" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
        <div class="balancy-feedback-comment-box" style="display: none;">
          <textarea class="balancy-feedback-textarea" placeholder="What could be improved? (optional)" maxlength="1000" rows="3"></textarea>
          <div class="balancy-feedback-comment-actions">
            <div class="balancy-feedback-char-counter">
              <span class="balancy-char-count">0/1000</span>
            </div>
            <div class="balancy-feedback-action-buttons">
              <button class="balancy-feedback-submit-button">Submit</button>
              <button class="balancy-feedback-cancel-button">Cancel</button>
            </div>
          </div>
        </div>
        <div class="balancy-feedback-error" style="display: none;"></div>
      </div>
    `;
    }
    /**
     * Initialize feedback handlers for all feedback components in container
     */
    initializeFeedbackHandlers(container) {
        const feedbackContainers = container.querySelectorAll('.balancy-message-feedback');
        feedbackContainers.forEach((feedbackContainer) => {
            const messageId = feedbackContainer.getAttribute('data-message-id');
            if (!messageId)
                return;
            // Load existing feedback for this message
            this.loadFeedback(messageId, feedbackContainer);
            // Positive button
            const positiveBtn = feedbackContainer.querySelector('.balancy-feedback-positive');
            positiveBtn?.addEventListener('click', () => this.handleFeedbackClick(messageId, 'positive', feedbackContainer));
            // Negative button
            const negativeBtn = feedbackContainer.querySelector('.balancy-feedback-negative');
            negativeBtn?.addEventListener('click', () => this.handleFeedbackClick(messageId, 'negative', feedbackContainer));
            // Submit button
            const submitBtn = feedbackContainer.querySelector('.balancy-feedback-submit-button');
            submitBtn?.addEventListener('click', () => this.handleCommentSubmit(messageId, feedbackContainer));
            // Cancel button
            const cancelBtn = feedbackContainer.querySelector('.balancy-feedback-cancel-button');
            cancelBtn?.addEventListener('click', () => this.handleCommentCancel(feedbackContainer));
            // Character counter
            const textarea = feedbackContainer.querySelector('.balancy-feedback-textarea');
            const charCounter = feedbackContainer.querySelector('.balancy-char-count');
            const submitButton = feedbackContainer.querySelector('.balancy-feedback-submit-button');
            textarea?.addEventListener('input', () => {
                const length = textarea.value.length;
                if (charCounter) {
                    charCounter.textContent = `${length}/1000`;
                    charCounter.className = 'balancy-char-count';
                    if (length >= 1000) {
                        charCounter.classList.add('char-limit-reached');
                    }
                    else if (length >= 900) {
                        charCounter.classList.add('char-limit-warning');
                    }
                }
                // Disable submit button if over limit
                if (submitButton) {
                    submitButton.disabled = length > 1000;
                }
            });
        });
    }
    /**
     * Load existing feedback for a message
     */
    async loadFeedback(messageId, container) {
        try {
            const apiBaseUrl = this.adapter.apiBaseUrl;
            const headers = this.adapter.getAuthHeaders();
            const response = await fetch(`${apiBaseUrl}/api/chat/messages/${messageId}/feedback`, {
                headers,
            });
            if (response.ok) {
                const data = await response.json();
                if (data.data) {
                    this.updateFeedbackUI(container, data.data.feedbackType);
                }
            }
        }
        catch (error) {
            console.error('[UIManager] Failed to load feedback:', error);
            // Silently fail - feedback loading is not critical
        }
    }
    /**
     * Handle feedback button click (thumbs up/down)
     */
    async handleFeedbackClick(messageId, type, container) {
        const positiveBtn = container.querySelector('.balancy-feedback-positive');
        const negativeBtn = container.querySelector('.balancy-feedback-negative');
        const commentBox = container.querySelector('.balancy-feedback-comment-box');
        // Check if clicking the same button (toggle off)
        const isActive = type === 'positive'
            ? positiveBtn?.classList.contains('active')
            : negativeBtn?.classList.contains('active');
        if (isActive) {
            // Delete feedback and hide comment box
            commentBox.style.display = 'none';
            await this.deleteFeedback(messageId, container);
            return;
        }
        // For negative feedback, submit immediately with empty comment, then show comment box
        if (type === 'negative') {
            // Submit negative feedback immediately (this overwrites any existing positive feedback)
            await this.submitFeedback(messageId, type, '', container);
            // Show comment box so user can optionally add a comment
            commentBox.style.display = 'block';
            const textarea = commentBox.querySelector('.balancy-feedback-textarea');
            textarea?.focus();
            return;
        }
        // For positive feedback, submit immediately (this will overwrite any existing negative feedback)
        commentBox.style.display = 'none';
        await this.submitFeedback(messageId, type, '', container);
    }
    /**
     * Handle comment submit
     */
    async handleCommentSubmit(messageId, container) {
        const textarea = container.querySelector('.balancy-feedback-textarea');
        const comment = textarea?.value || '';
        const commentBox = container.querySelector('.balancy-feedback-comment-box');
        await this.submitFeedback(messageId, 'negative', comment, container);
        // Hide comment box after successful submission
        commentBox.style.display = 'none';
    }
    /**
     * Handle comment cancel
     */
    handleCommentCancel(container) {
        const commentBox = container.querySelector('.balancy-feedback-comment-box');
        const textarea = container.querySelector('.balancy-feedback-textarea');
        commentBox.style.display = 'none';
        if (textarea) {
            textarea.value = '';
        }
        // Clear feedback UI if no feedback was submitted
        this.updateFeedbackUI(container, null);
    }
    /**
     * Submit feedback to API
     */
    async submitFeedback(messageId, type, comment, container) {
        const errorDiv = container.querySelector('.balancy-feedback-error');
        const commentBox = container.querySelector('.balancy-feedback-comment-box');
        // Clear previous error
        errorDiv.style.display = 'none';
        errorDiv.textContent = '';
        // Disable buttons during submission
        this.setFeedbackLoading(container, true);
        try {
            const apiBaseUrl = this.adapter.apiBaseUrl;
            const headers = this.adapter.getAuthHeaders();
            const response = await fetch(`${apiBaseUrl}/api/chat/messages/${messageId}/feedback`, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    feedbackType: type,
                    comment: comment.trim() || undefined,
                }),
            });
            if (!response.ok) {
                throw new Error('Failed to submit feedback');
            }
            // Update UI to show active state
            this.updateFeedbackUI(container, type);
            // Clear textarea (but don't hide comment box - let caller decide)
            const textarea = commentBox.querySelector('.balancy-feedback-textarea');
            if (textarea) {
                textarea.value = '';
            }
            console.log('[UIManager] Feedback submitted successfully');
        }
        catch (error) {
            console.error('[UIManager] Failed to submit feedback:', error);
            errorDiv.textContent =
                error instanceof Error ? error.message : 'Failed to submit feedback';
            errorDiv.style.display = 'block';
        }
        finally {
            this.setFeedbackLoading(container, false);
        }
    }
    /**
     * Delete feedback from API
     */
    async deleteFeedback(messageId, container) {
        const errorDiv = container.querySelector('.balancy-feedback-error');
        // Clear previous error
        errorDiv.style.display = 'none';
        errorDiv.textContent = '';
        // Disable buttons during deletion
        this.setFeedbackLoading(container, true);
        try {
            const apiBaseUrl = this.adapter.apiBaseUrl;
            const headers = this.adapter.getAuthHeaders();
            const response = await fetch(`${apiBaseUrl}/api/chat/messages/${messageId}/feedback`, {
                method: 'DELETE',
                headers,
            });
            if (!response.ok && response.status !== 404) {
                throw new Error('Failed to delete feedback');
            }
            // Clear UI
            this.updateFeedbackUI(container, null);
            console.log('[UIManager] Feedback deleted successfully');
        }
        catch (error) {
            console.error('[UIManager] Failed to delete feedback:', error);
            errorDiv.textContent =
                error instanceof Error ? error.message : 'Failed to remove feedback';
            errorDiv.style.display = 'block';
        }
        finally {
            this.setFeedbackLoading(container, false);
        }
    }
    /**
     * Update feedback UI to show active state
     */
    updateFeedbackUI(container, type) {
        const positiveBtn = container.querySelector('.balancy-feedback-positive');
        const negativeBtn = container.querySelector('.balancy-feedback-negative');
        positiveBtn?.classList.remove('active');
        negativeBtn?.classList.remove('active');
        if (type === 'positive') {
            positiveBtn?.classList.add('active');
        }
        else if (type === 'negative') {
            negativeBtn?.classList.add('active');
        }
    }
    /**
     * Set loading state for feedback buttons
     */
    setFeedbackLoading(container, loading) {
        const buttons = container.querySelectorAll('button');
        buttons.forEach((btn) => {
            if (loading) {
                btn.setAttribute('disabled', 'true');
            }
            else {
                btn.removeAttribute('disabled');
            }
        });
    }
    getAuthenticatedPanelHTML(iconUrl, user) {
        const displayName = user.email || user.name;
        const currentChannel = this.channels.find((ch) => ch.id === this.currentChannelId);
        const totalTokens = currentChannel?.totalTokens || 0;
        const channelOptions = this.channels.length > 0
            ? this.channels
                .map((ch) => {
                const usage = Math.round((ch.totalTokens / 100000) * 100);
                const timeStr = this.formatRelativeTime(ch.lastMessageAt);
                const timeDisplay = timeStr ? ` • ${timeStr}` : '';
                return `<option value="${ch.id}" ${ch.id === this.currentChannelId ? 'selected' : ''}>
            ${ch.mode === 'documentation' ? '🔵' : '🟢'} ${this.escapeHTML(ch.name)} (${ch.messageCount}) • ${usage}%${timeDisplay}
          </option>`;
            })
                .join('')
            : '<option value="">No conversations</option>';
        return `
      <div class="balancy-panel-header">
        <div class="balancy-panel-avatar online">
          <img class="balancy-avatar-img" src="${iconUrl}" alt="AI-Assistant">
        </div>
        <div class="balancy-panel-info">
          <div class="balancy-panel-title">Nia${this.version ? ` <span class="balancy-version">v${this.version}</span>` : ''}</div>
          <div class="balancy-panel-status">${this.escapeHTML(displayName)}</div>
        </div>
        <div class="balancy-panel-actions">
          <button class="balancy-panel-action-btn balancy-mcp-btn ${this.mcpStatus === 'connected' ? 'active' : ''}" id="balancy-mcp-btn" title="External AI${this.mcpStatus === 'connected' ? ' (Connected)' : ''}">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.49 8.49l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.49-8.49l2.83-2.83"></path>
              <circle cx="12" cy="12" r="3"></circle>
            </svg>
          </button>
          <button class="balancy-panel-action-btn balancy-width-toggle-btn" id="balancy-width-toggle" title="Toggle width">⇔</button>
          <button class="balancy-panel-action-btn balancy-position-toggle-btn" id="balancy-position-toggle" title="Move to ${this.isLeftMode ? 'right' : 'left'}">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              ${this.isLeftMode
            ? '<polyline points="9 18 15 12 9 6"></polyline>'
            : '<polyline points="15 18 9 12 15 6"></polyline>'}
            </svg>
          </button>
          <button class="balancy-panel-action-btn balancy-close-btn" id="balancy-close-panel" title="Close">✕</button>
        </div>
      </div>

      <div class="balancy-channel-switcher">
        <span class="balancy-context-badge" id="balancy-context-badge">${this.getContextBadgeHTML()}</span>
        <div class="balancy-channel-select-wrapper">
          <select class="balancy-channel-select" id="balancy-channel-select">
            ${channelOptions}
          </select>
        </div>
        <button class="balancy-delete-channel-btn" id="balancy-delete-channel" title="Delete channel">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="3 6 5 6 21 6"></polyline>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
          </svg>
        </button>
        <div class="balancy-new-channel-wrapper">
          <button class="balancy-new-channel-btn" id="balancy-new-channel">+ New</button>
          <div class="balancy-context-dropdown" id="balancy-context-dropdown">
            ${UIManager.CHAT_CONTEXTS.map((ctx) => `
              <button class="balancy-context-option" data-context="${ctx.id}">
                <span class="balancy-context-dot" style="background: ${ctx.color}"></span>
                <span>${ctx.label}</span>
              </button>
            `).join('')}
          </div>
        </div>
      </div>

      <!-- Delete Confirmation Modal -->
      <div class="balancy-delete-modal-overlay" id="balancy-delete-modal">
        <div class="balancy-delete-modal">
          <div class="balancy-delete-modal-header">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <span class="balancy-delete-modal-title">Delete Conversation</span>
          </div>
          <p class="balancy-delete-modal-message">
            Are you sure you want to delete <strong id="balancy-delete-channel-name"></strong>? All messages will be permanently removed.
          </p>
          <div class="balancy-delete-modal-actions">
            <button class="balancy-delete-modal-cancel" id="balancy-delete-cancel">Cancel</button>
            <button class="balancy-delete-modal-confirm" id="balancy-delete-confirm">Delete</button>
          </div>
        </div>
      </div>

      <div class="balancy-usage-indicator">
        <div class="balancy-credit-badge balancy-credit-green" id="balancy-credit-badge" title="AI Tokens - Click for details">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v12M6 12h12" stroke-linecap="round"/></svg>
          <span class="balancy-credit-value">--</span>
        </div>
        ${this.createUsageProgress(totalTokens)}
        <div class="balancy-usage-stats">
          <div class="balancy-usage-stat">
            <span class="balancy-usage-label">Messages:</span>
            <span class="balancy-usage-value">${currentChannel?.messageCount || 0}</span>
          </div>
          <div class="balancy-usage-stat">
            <span class="balancy-usage-label">LLM Tokens:</span>
            <span class="balancy-usage-value">${this.formatTokens(totalTokens)}</span>
          </div>
          <div class="balancy-usage-stat">
            <span class="balancy-usage-label">Credits:</span>
            <span class="balancy-usage-value" id="balancy-credits-used">${currentChannel?.creditsUsed || 0}</span>
          </div>
        </div>
      </div>

      <div class="balancy-panel-content">
        <div class="balancy-chat-area" id="balancy-chat-area">
          <div class="balancy-chat-messages" id="balancy-messages">
            <!-- Messages will be rendered here -->
          </div>

          <div class="balancy-typing-indicator" id="balancy-typing-indicator">
            <div class="balancy-typing-dots">
              <div class="balancy-typing-dot"></div>
              <div class="balancy-typing-dot"></div>
              <div class="balancy-typing-dot"></div>
            </div>
            <span style="color: rgba(255,255,255,0.7); font-size: 0.75rem; margin-left: 4px;">Thinking...</span>
          </div>
        </div>

        <div class="balancy-chat-input-area">
          ${this.getModelSelectorHTML()}
          <div class="balancy-input-container">
            <textarea class="balancy-chat-input" id="balancy-input" placeholder="Ask me anything..." rows="1"></textarea>
            <div class="balancy-input-actions">
              <button class="balancy-input-action" id="balancy-send" title="Send message">🚀</button>
            </div>
          </div>
        </div>
      </div>
    `;
    }
    getUnauthenticatedPanelHTML(iconUrl) {
        return `
      <div class="balancy-panel-header">
        <div class="balancy-panel-avatar offline">
          <img class="balancy-avatar-img" src="${iconUrl}" alt="AI-Assistant">
        </div>
        <div class="balancy-panel-info">
          <div class="balancy-panel-title">Nia${this.version ? ` <span class="balancy-version">v${this.version}</span>` : ''}</div>
          <div class="balancy-panel-status">Not Authenticated</div>
        </div>
        <div class="balancy-panel-actions">
          <button class="balancy-panel-action-btn balancy-mcp-btn ${this.mcpStatus === 'connected' ? 'active' : ''}" id="balancy-mcp-btn" title="External AI${this.mcpStatus === 'connected' ? ' (Connected)' : ''}">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.49 8.49l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.49-8.49l2.83-2.83"></path>
              <circle cx="12" cy="12" r="3"></circle>
            </svg>
          </button>
          <button class="balancy-panel-action-btn balancy-width-toggle-btn" id="balancy-width-toggle" title="Toggle width">⇔</button>
          <button class="balancy-panel-action-btn balancy-position-toggle-btn" id="balancy-position-toggle" title="Move to ${this.isLeftMode ? 'right' : 'left'}">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              ${this.isLeftMode
            ? '<polyline points="9 18 15 12 9 6"></polyline>'
            : '<polyline points="15 18 9 12 15 6"></polyline>'}
            </svg>
          </button>
          <button class="balancy-panel-action-btn balancy-close-btn" id="balancy-close-panel" title="Close">✕</button>
        </div>
      </div>

      <div class="balancy-auth-container">
        <div class="balancy-auth-content">
          <h2 class="balancy-auth-title">Welcome to Nia!</h2>
          <p class="balancy-auth-description">
            Sign in to access AI-powered game development assistance.
          </p>
          <button class="balancy-auth-btn" id="balancy-auth">
            Sign in
          </button>
        </div>
      </div>
    `;
    }
    getAuthErrorPanelHTML(iconUrl, message) {
        const escapedMessage = this.escapeHTML(message);
        return `
      <div class="balancy-panel-header">
        <div class="balancy-panel-avatar offline">
          <img class="balancy-avatar-img" src="${iconUrl}" alt="AI-Assistant">
        </div>
        <div class="balancy-panel-info">
          <div class="balancy-panel-title">Nia${this.version ? ` <span class="balancy-version">v${this.version}</span>` : ''}</div>
          <div class="balancy-panel-status" style="color: #e74c3c;">Authentication Failed</div>
        </div>
        <div class="balancy-panel-actions">
          <button class="balancy-panel-action-btn balancy-close-btn" id="balancy-close-panel" title="Close">&#10005;</button>
        </div>
      </div>

      <div class="balancy-auth-container">
        <div class="balancy-auth-content">
          <div style="font-size: 32px; margin-bottom: 12px;">&#9888;</div>
          <h2 class="balancy-auth-title">Connection Error</h2>
          <p class="balancy-auth-description">${escapedMessage}</p>
          <button class="balancy-auth-btn" id="balancy-auth-retry">Retry</button>
        </div>
      </div>
    `;
    }
    /**
     * Generate HTML for the model selector row (above the input textarea).
     * Shows model pills when multiple tiers are available.
     * Shows current model label when a channel is active.
     */
    getModelSelectorHTML() {
        if (this.modelTiers.length <= 1)
            return '';
        const currentChannel = this.channels.find((ch) => ch.id === this.currentChannelId);
        // If a channel is active, show its model as a static label
        if (currentChannel) {
            const tier = this.modelTiers.find((t) => t.modelId === currentChannel.model);
            const label = tier?.displayName || 'Other';
            return `<div class="balancy-model-indicator" id="balancy-model-indicator">${this.escapeHTML(label)}</div>`;
        }
        // No active channel: show selectable model pills
        const pills = this.modelTiers
            .map((tier) => `<button class="balancy-model-pill ${this.selectedModel === tier.modelId ? 'active' : ''}"
                  data-model="${this.escapeHTML(tier.modelId)}"
                  title="${this.escapeHTML(tier.description)}">
            <span class="balancy-model-pill-name">${this.escapeHTML(tier.displayName)}</span>
          </button>`)
            .join('');
        return `<div class="balancy-model-selector" id="balancy-model-selector">${pills}</div>`;
    }
    /**
     * Set up click handlers for model selector pills
     */
    setupModelSelector() {
        const selector = this.chatPanel?.querySelector('#balancy-model-selector');
        if (!selector)
            return;
        selector.querySelectorAll('.balancy-model-pill').forEach((pill) => {
            pill.addEventListener('click', () => {
                const modelId = pill.dataset.model;
                if (!modelId || modelId === this.selectedModel)
                    return;
                this.selectedModel = modelId;
                localStorage.setItem(UIManager.MODEL_KEY, modelId);
                // Update active state on all pills
                selector.querySelectorAll('.balancy-model-pill').forEach((p) => {
                    p.classList.toggle('active', p.dataset.model === modelId);
                });
                console.log(`[UIManager] Model selected: ${modelId}`);
            });
        });
    }
    /**
     * Refresh the model selector UI (e.g., after channel switch)
     */
    refreshModelSelector() {
        const inputArea = this.chatPanel?.querySelector('.balancy-chat-input-area');
        if (!inputArea)
            return;
        // Remove existing model selector / indicator
        const existing = inputArea.querySelector('#balancy-model-selector') ||
            inputArea.querySelector('#balancy-model-indicator');
        if (existing)
            existing.remove();
        // Insert new one before the input container
        const newHTML = this.getModelSelectorHTML();
        if (newHTML) {
            const temp = document.createElement('div');
            temp.innerHTML = newHTML;
            const newEl = temp.firstElementChild;
            if (newEl) {
                const inputContainer = inputArea.querySelector('.balancy-input-container');
                inputArea.insertBefore(newEl, inputContainer);
                this.setupModelSelector();
            }
        }
    }
    setupPanelEventListeners() {
        const closeBtn = this.chatPanel?.querySelector('#balancy-close-panel');
        const widthToggleBtn = this.chatPanel?.querySelector('#balancy-width-toggle');
        const positionToggleBtn = this.chatPanel?.querySelector('#balancy-position-toggle');
        const authBtn = this.chatPanel?.querySelector('#balancy-auth');
        const sendBtn = this.chatPanel?.querySelector('#balancy-send');
        const input = this.chatPanel?.querySelector('#balancy-input');
        const channelSelect = this.chatPanel?.querySelector('#balancy-channel-select');
        const newChannelBtn = this.chatPanel?.querySelector('#balancy-new-channel');
        const deleteChannelBtn = this.chatPanel?.querySelector('#balancy-delete-channel');
        const deleteModal = this.chatPanel?.querySelector('#balancy-delete-modal');
        const deleteCancelBtn = this.chatPanel?.querySelector('#balancy-delete-cancel');
        const deleteConfirmBtn = this.chatPanel?.querySelector('#balancy-delete-confirm');
        // Analytics button
        const analyticsBtn = this.chatPanel?.querySelector('#balancy-analytics-btn');
        analyticsBtn?.addEventListener('click', () => {
            // TODO: Before showing analytics, verify user has permission for this game.
            // Currently trusting that authenticated users have access.
            // Future: Call GET /analytics/games/:gameId/permissions to check access.
            console.warn('[UIManager] Analytics opened — permission check not yet implemented');
            this.showAnalytics(this.assetsBaseUrl);
        });
        closeBtn?.addEventListener('click', () => this.togglePanel());
        widthToggleBtn?.addEventListener('click', () => this.toggleWidth());
        positionToggleBtn?.addEventListener('click', () => {
            this.togglePosition();
            // Update button icon and title to reflect new state
            if (positionToggleBtn) {
                positionToggleBtn.setAttribute('title', `Move to ${this.isLeftMode ? 'right' : 'left'}`);
                positionToggleBtn.innerHTML = `
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            ${this.isLeftMode
                    ? '<polyline points="9 18 15 12 9 6"></polyline>'
                    : '<polyline points="15 18 9 12 15 6"></polyline>'}
          </svg>
        `;
            }
        });
        authBtn?.addEventListener('click', () => this.onAuthClick());
        sendBtn?.addEventListener('click', () => this.onSendMessage());
        // New channel context selector dropdown
        newChannelBtn?.addEventListener('click', (e) => {
            e.stopPropagation();
            const dropdown = this.chatPanel?.querySelector('#balancy-context-dropdown');
            dropdown?.classList.toggle('active');
        });
        // Context option click handlers
        const contextOptions = this.chatPanel?.querySelectorAll('.balancy-context-option');
        contextOptions?.forEach((option) => {
            option.addEventListener('click', (e) => {
                e.stopPropagation();
                const context = option.dataset.context;
                const dropdown = this.chatPanel?.querySelector('#balancy-context-dropdown');
                dropdown?.classList.remove('active');
                this.onNewChannelWithContext(context);
            });
        });
        // Close dropdown when clicking outside
        document.addEventListener('click', () => {
            const dropdown = this.chatPanel?.querySelector('#balancy-context-dropdown');
            dropdown?.classList.remove('active');
        });
        // MCP bridge button
        const mcpBtn = this.chatPanel?.querySelector('#balancy-mcp-btn');
        mcpBtn?.addEventListener('click', () => {
            if (this.currentView === 'mcp') {
                this.hideMCPView();
            }
            else {
                this.showMCPView();
            }
        });
        // Credit badge toggle
        const creditBadge = this.chatPanel?.querySelector('#balancy-credit-badge');
        creditBadge?.addEventListener('click', () => {
            if (this.currentView === 'credits') {
                this.hideCreditsView();
            }
            else {
                this.showCreditsView();
            }
        });
        // Model selector
        this.setupModelSelector();
        // Delete channel flow
        deleteChannelBtn?.addEventListener('click', () => {
            if (!this.currentChannelId)
                return;
            const channel = this.channels.find((ch) => ch.id === this.currentChannelId);
            const nameEl = this.chatPanel?.querySelector('#balancy-delete-channel-name');
            if (nameEl) {
                nameEl.textContent = channel?.name || 'this conversation';
            }
            deleteModal?.classList.add('active');
        });
        deleteCancelBtn?.addEventListener('click', () => {
            deleteModal?.classList.remove('active');
        });
        deleteModal?.addEventListener('click', (e) => {
            if (e.target === deleteModal) {
                deleteModal?.classList.remove('active');
            }
        });
        deleteConfirmBtn?.addEventListener('click', () => this.onDeleteChannel());
        channelSelect?.addEventListener('change', async (e) => {
            const newChannelId = e.target.value;
            if (newChannelId && newChannelId !== this.currentChannelId) {
                this.currentChannelId = newChannelId;
                // Save to localStorage
                localStorage.setItem(UIManager.LAST_CHANNEL_KEY, newChannelId);
                // Update adapter's current channel
                if ('setCurrentChannel' in this.adapter) {
                    this.adapter.setCurrentChannel(newChannelId);
                }
                // Restore context cache and optimization flag for this channel
                const channel = this.channels.find((ch) => ch.id === newChannelId);
                if (channel) {
                    this.syncChannelCacheState(channel);
                    // Restore chat context from channel
                    this.selectedChatContext = channel.chatContext || 'cms';
                }
                await this.loadChannelMessages(newChannelId);
                this.updateUsageIndicator();
                // Refresh model selector to show current channel's model
                this.refreshModelSelector();
                // Update optimization toggle UI
                this.updateOptimizationToggle();
                // Update border color for channel context
                this.updateContextBorder();
                // Focus input after channel switch
                input?.focus();
            }
        });
        // When dropdown closes (blur), always return focus to input
        channelSelect?.addEventListener('blur', () => {
            input?.focus();
        });
        input?.addEventListener('keypress', (e) => {
            const keyEvent = e;
            if (keyEvent.key === 'Enter' && !keyEvent.shiftKey) {
                e.preventDefault();
                this.onSendMessage();
            }
        });
        // Auto-resize textarea
        input?.addEventListener('input', () => {
            input.style.height = 'auto';
            input.style.height = Math.min(input.scrollHeight, 100) + 'px';
        });
    }
    togglePanel() {
        if (!this.chatPanel)
            return;
        this.panelOpen = !this.panelOpen;
        this.chatPanel.classList.toggle('active', this.panelOpen);
        // Focus input when panel opens
        if (this.panelOpen) {
            const input = this.chatPanel?.querySelector('#balancy-input');
            // Use setTimeout to allow CSS transition to complete
            setTimeout(() => input?.focus(), 100);
        }
        console.log('[UIManager] Panel', this.panelOpen ? 'opened' : 'closed');
    }
    toggleWidth() {
        if (!this.chatPanel)
            return;
        this.isWideMode = !this.isWideMode;
        this.chatPanel.classList.toggle('wide-mode', this.isWideMode);
        // Save to localStorage
        localStorage.setItem(UIManager.PANEL_WIDTH_KEY, this.isWideMode ? 'wide' : 'normal');
        console.log('[UIManager] Panel width', this.isWideMode ? 'wide (840px)' : 'normal (420px)');
    }
    togglePosition() {
        if (!this.chatPanel)
            return;
        this.isLeftMode = !this.isLeftMode;
        this.chatPanel.classList.toggle('left-mode', this.isLeftMode);
        // Update floating icon position
        if (this.floatingIcon) {
            this.floatingIcon.style.right = this.isLeftMode ? 'auto' : '24px';
            this.floatingIcon.style.left = this.isLeftMode ? '24px' : 'auto';
        }
        // Save to localStorage
        localStorage.setItem(UIManager.PANEL_POSITION_KEY, this.isLeftMode ? 'left' : 'right');
        console.log('[UIManager] Panel position', this.isLeftMode ? 'left' : 'right');
    }
    async updatePanelWithUser(user) {
        if (!this.chatPanel)
            return;
        const wasOpen = this.panelOpen;
        this.removePanel();
        try {
            await this.createChatPanel(user);
        }
        catch (error) {
            console.error('[UIManager] Failed to create authenticated panel, falling back:', error);
            // Ensure we at least have a panel even if channel loading failed
            if (!this.chatPanel) {
                await this.createChatPanel(null);
            }
        }
        if (wasOpen && this.chatPanel) {
            this.chatPanel.classList.add('active');
            this.panelOpen = true;
        }
    }
    /**
     * Show an authentication error panel with a retry button.
     * Replaces the current panel content with an error message.
     */
    showAuthError(message, retryCallback) {
        this.initShadowDOM();
        const wasOpen = this.panelOpen;
        this.removePanel();
        const iconUrl = this.adapter.getAssetURL('icons/floating-button.png');
        this.chatPanel = document.createElement('div');
        this.chatPanel.className = 'balancy-chat-panel';
        this.chatPanel.style.pointerEvents = 'auto';
        this.chatPanel.innerHTML = this.getAuthErrorPanelHTML(iconUrl, message);
        this.shadowRoot.appendChild(this.chatPanel);
        this.setupPanelEventListeners();
        // Load position preference
        if (this.isLeftMode) {
            this.chatPanel.classList.add('left-mode');
        }
        // Wire up retry button
        const retryBtn = this.chatPanel.querySelector('#balancy-auth-retry');
        if (retryBtn) {
            retryBtn.addEventListener('click', () => {
                console.log('[UIManager] Auth retry clicked');
                retryCallback();
            });
        }
        if (wasOpen && this.chatPanel) {
            this.chatPanel.classList.add('active');
            this.panelOpen = true;
        }
        console.log('[UIManager] Auth error panel shown');
    }
    /**
     * Show the terms acceptance / welcome screen for first-time users.
     * Replaces the current panel with a welcome message and "Get Started" button.
     */
    showTermsAcceptance(user, onAccept) {
        this.initShadowDOM();
        const wasOpen = this.panelOpen;
        this.removePanel();
        const iconUrl = this.adapter.getAssetURL('icons/floating-button.png');
        this.chatPanel = document.createElement('div');
        this.chatPanel.className = 'balancy-chat-panel';
        this.chatPanel.style.pointerEvents = 'auto';
        this.chatPanel.innerHTML = this.getTermsAcceptancePanelHTML(iconUrl, user);
        this.shadowRoot.appendChild(this.chatPanel);
        this.setupPanelEventListeners();
        // Load position preference
        if (this.isLeftMode) {
            this.chatPanel.classList.add('left-mode');
        }
        // Wire up the "Get Started" button
        const acceptBtn = this.chatPanel.querySelector('#balancy-terms-accept');
        if (acceptBtn) {
            acceptBtn.addEventListener('click', () => {
                // Disable button to prevent double-submit
                acceptBtn.disabled = true;
                acceptBtn.textContent = 'Starting...';
                onAccept();
            });
        }
        if (wasOpen && this.chatPanel) {
            this.chatPanel.classList.add('active');
            this.panelOpen = true;
        }
        console.log('[UIManager] Terms acceptance panel shown');
    }
    getTermsAcceptancePanelHTML(iconUrl, user) {
        const firstName = this.escapeHTML((user.name || 'there').split(' ')[0]);
        return `
      <div class="balancy-panel-header">
        <div class="balancy-panel-avatar online">
          <img class="balancy-avatar-img" src="${iconUrl}" alt="AI-Assistant">
        </div>
        <div class="balancy-panel-info">
          <div class="balancy-panel-title">Nia${this.version ? ` <span class="balancy-version">v${this.version}</span>` : ''}</div>
          <div class="balancy-panel-status">Welcome!</div>
        </div>
        <div class="balancy-panel-actions">
          <button class="balancy-panel-action-btn balancy-close-btn" id="balancy-close-panel" title="Close">&#10005;</button>
        </div>
      </div>

      <div class="balancy-terms-container">
        <div class="balancy-terms-content">
          <div class="balancy-terms-avatar">
            <img class="balancy-terms-avatar-img" src="${iconUrl}" alt="Nia">
          </div>
          <h2 class="balancy-terms-title">Hi, ${firstName}!</h2>
          <p class="balancy-terms-description">
            I'm <strong>Nia</strong>, your AI-powered Balancy assistant.
            I can help you manage game data, set up events and offers,
            write extension scripts, and answer questions about the platform.
          </p>
          <p class="balancy-terms-disclaimer">
            AI-generated responses may not always be accurate. I'm improving every day.
          </p>
          <button class="balancy-terms-accept-btn" id="balancy-terms-accept">Get Started</button>
        </div>
      </div>
    `;
    }
    removePanel() {
        this.chatPanel?.remove();
        this.chatPanel = null;
        this.panelOpen = false;
    }
    async onAuthClick() {
        try {
            const result = await this.adapter.requestAuth();
            if (result.success && result.user) {
                await this.updatePanelWithUser(result.user);
            }
            else {
                this.addMessage(`Authentication failed: ${result.error || 'Unknown error'}`, 'assistant');
            }
        }
        catch (error) {
            console.error('[UIManager] Auth error:', error);
            this.addMessage('Authentication error. Please try again.', 'assistant');
        }
    }
    async onNewChannel() {
        this.onNewChannelWithContext('cms');
    }
    async onNewChannelWithContext(context) {
        this.selectedChatContext = context;
        localStorage.setItem(UIManager.CONTEXT_KEY, context);
        this.currentChannelId = null;
        this.messages = [];
        // Update adapter's current channel
        if ('setCurrentChannel' in this.adapter) {
            this.adapter.setCurrentChannel(null);
        }
        const messagesContainer = this.chatPanel?.querySelector('#balancy-messages');
        if (messagesContainer) {
            messagesContainer.innerHTML = '';
        }
        // Update channel selector
        const channelSelect = this.chatPanel?.querySelector('#balancy-channel-select');
        if (channelSelect) {
            channelSelect.value = '';
        }
        this.updateUsageIndicator();
        // Show welcome message for new conversation
        this.showWelcomeMessage();
        // Refresh model selector to show pills (no active channel)
        this.refreshModelSelector();
        // Update panel border color for context
        this.updateContextBorder();
        // Focus input for new conversation
        const input = this.chatPanel?.querySelector('#balancy-input');
        input?.focus();
        console.log(`[UIManager] Started new conversation with context: ${context}`);
    }
    async onDeleteChannel() {
        if (!this.currentChannelId)
            return;
        const deleteModal = this.chatPanel?.querySelector('#balancy-delete-modal');
        const confirmBtn = this.chatPanel?.querySelector('#balancy-delete-confirm');
        // Show loading state
        if (confirmBtn) {
            confirmBtn.disabled = true;
            confirmBtn.textContent = 'Deleting...';
        }
        try {
            const headers = this.adapter.getAuthHeaders();
            const response = await fetch(`${this.adapter.apiBaseUrl}/api/chat/channels/${this.currentChannelId}`, {
                method: 'DELETE',
                headers,
            });
            if (!response.ok) {
                throw new Error(`Failed to delete channel: ${response.status}`);
            }
            console.log(`[UIManager] Channel deleted: ${this.currentChannelId}`);
            // Remove from local channels array
            this.channels = this.channels.filter((ch) => ch.id !== this.currentChannelId);
            // Clear localStorage reference if it was the last channel
            localStorage.removeItem(UIManager.LAST_CHANNEL_KEY);
            // Hide modal
            deleteModal?.classList.remove('active');
            // Switch to another channel or clear
            if (this.channels.length > 0) {
                this.currentChannelId = this.channels[0].id;
                localStorage.setItem(UIManager.LAST_CHANNEL_KEY, this.currentChannelId);
                if ('setCurrentChannel' in this.adapter) {
                    this.adapter.setCurrentChannel(this.currentChannelId);
                }
                await this.loadChannelMessages(this.currentChannelId);
            }
            else {
                this.currentChannelId = null;
                this.messages = [];
                if ('setCurrentChannel' in this.adapter) {
                    this.adapter.setCurrentChannel(null);
                }
                const messagesContainer = this.chatPanel?.querySelector('#balancy-messages');
                if (messagesContainer) {
                    messagesContainer.innerHTML = '';
                }
            }
            // Update channel selector dropdown
            this.refreshChannelSelect();
            this.updateUsageIndicator();
            // Focus input after channel switch
            const input = this.chatPanel?.querySelector('#balancy-input');
            input?.focus();
        }
        catch (error) {
            console.error('[UIManager] Failed to delete channel:', error);
            alert(error instanceof Error ? error.message : 'Failed to delete channel');
        }
        finally {
            // Reset button state
            if (confirmBtn) {
                confirmBtn.disabled = false;
                confirmBtn.textContent = 'Delete';
            }
            deleteModal?.classList.remove('active');
        }
    }
    refreshChannelSelect() {
        const channelSelect = this.chatPanel?.querySelector('#balancy-channel-select');
        if (!channelSelect)
            return;
        if (this.channels.length > 0) {
            const contextDots = {
                cms: '\u{1F7E2}',
                'sdk-unity': '\u{1F535}',
                'sdk-ts': '\u{1F7E1}',
            };
            // Flat list sorted by most recent first (channels already sorted by lastMessageAt from server)
            let html = '';
            for (const ch of this.channels) {
                const dot = contextDots[(ch.chatContext || 'cms')] ||
                    contextDots.cms;
                const usage = Math.round((ch.totalTokens / 100000) * 100);
                const timeStr = this.formatRelativeTime(ch.lastMessageAt);
                const timeDisplay = timeStr ? ` \u2022 ${timeStr}` : '';
                html += `<option value="${ch.id}" ${ch.id === this.currentChannelId ? 'selected' : ''}>
          ${dot} ${this.escapeHTML(ch.name)} (${ch.messageCount}) \u2022 ${usage}%${timeDisplay}
        </option>`;
            }
            channelSelect.innerHTML =
                html || '<option value="">No conversations</option>';
        }
        else {
            channelSelect.innerHTML = '<option value="">No conversations</option>';
        }
    }
    getActiveContext() {
        if (this.currentChannelId) {
            const ch = this.channels.find((c) => c.id === this.currentChannelId);
            return ch?.chatContext || 'cms';
        }
        return this.selectedChatContext;
    }
    updateContextBorder() {
        if (!this.chatPanel)
            return;
        const ctx = this.getActiveContext();
        const colors = {
            cms: '#4CAF50',
            'sdk-unity': '#667eea',
            'sdk-ts': '#e6a817',
        };
        this.chatPanel.style.borderLeftColor = colors[ctx] || colors.cms;
        this.chatPanel.style.borderLeftWidth = '3px';
        this.chatPanel.style.borderLeftStyle = 'solid';
        // Update the context badge in the channel switcher
        const badge = this.chatPanel?.querySelector('#balancy-context-badge');
        if (badge) {
            badge.innerHTML = this.getContextBadgeHTML();
        }
    }
    getContextBadgeHTML() {
        const ctx = this.getActiveContext();
        const info = UIManager.CHAT_CONTEXTS.find((c) => c.id === ctx) ||
            UIManager.CHAT_CONTEXTS[0];
        return `<span class="balancy-context-dot" style="background: ${info.color}"></span>${info.label}`;
    }
    updateUsageIndicator() {
        const currentChannel = this.channels.find((ch) => ch.id === this.currentChannelId);
        const messageCountEl = this.chatPanel?.querySelector('.balancy-usage-stat:nth-child(1) .balancy-usage-value');
        const tokensEl = this.chatPanel?.querySelector('.balancy-usage-stat:nth-child(2) .balancy-usage-value');
        const creditsUsedEl = this.chatPanel?.querySelector('#balancy-credits-used');
        const usageCircleContainer = this.chatPanel?.querySelector('.balancy-usage-indicator');
        if (messageCountEl) {
            messageCountEl.textContent = (currentChannel?.messageCount || 0).toString();
        }
        if (tokensEl) {
            tokensEl.textContent = this.formatTokens(currentChannel?.totalTokens || 0);
        }
        if (creditsUsedEl) {
            creditsUsedEl.textContent = `${currentChannel?.creditsUsed || 0}`;
        }
        // Update circular progress indicator
        if (usageCircleContainer) {
            const existingCircle = usageCircleContainer.querySelector('.balancy-usage-circle');
            if (existingCircle) {
                const totalTokens = currentChannel?.totalTokens || 0;
                const newCircle = this.createUsageProgress(totalTokens);
                existingCircle.outerHTML = newCircle;
            }
        }
    }
    // Platform selector removed — platform is now determined by chatContext
    async onSendMessage() {
        const input = this.chatPanel?.querySelector('#balancy-input');
        const message = input?.value.trim();
        if (!message)
            return;
        // Hide welcome message on first send
        this.hideWelcomeMessage();
        // Remove any existing suggestion chips
        this.chatPanel
            ?.querySelectorAll('.balancy-suggestion-chips')
            .forEach((c) => c.remove());
        // Add user message immediately
        this.addMessage(message, 'user');
        input.value = '';
        input.style.height = 'auto';
        // Reset execution plan state for new message
        this.currentPlanElement = null;
        this.currentPlanSteps = [];
        // Show typing indicator with default text
        const typingIndicator = this.chatPanel?.querySelector('#balancy-typing-indicator');
        typingIndicator?.classList.add('show');
        this.setTypingText('Thinking...');
        try {
            const context = this.adapter.getContext();
            // Pass selected model and chatContext via userContext
            if ('setUserContextData' in this.adapter) {
                const currentData = this.adapter.userContextData || {};
                // Remove legacy platform field — platform is now determined by chatContext on the server
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { platform: _legacyPlatform, ...cleanData } = currentData;
                const contextData = {
                    ...cleanData,
                    model: this.selectedModel,
                    chatContext: this.selectedChatContext,
                };
                this.adapter.setUserContextData(contextData);
            }
            console.log('[UIManager] Sending chat message:', {
                message,
                currentChannelId: this.currentChannelId,
                model: this.selectedModel,
            });
            const response = await this.adapter.sendMessage(message, context);
            // Hide typing indicator
            typingIndicator?.classList.remove('show');
            if (response.success && response.reply) {
                // Extract suggestions: prefer server-provided, fallback to client-side parsing
                let suggestions = response.suggestions;
                let replyText = response.reply;
                if (!suggestions || suggestions.length === 0) {
                    // Client-side fallback: parse suggestions from reply text
                    const match = replyText.match(/<!--\s*suggestions:\s*(\[.*?\])\s*-->/s);
                    if (match) {
                        try {
                            suggestions = JSON.parse(match[1]);
                            replyText = replyText.replace(match[0], '').trimEnd();
                        }
                        catch (e) {
                            // Ignore parse errors
                        }
                    }
                }
                const messageDiv = this.addMessage(replyText, 'assistant', response.usage, response.messageId);
                // Render follow-up suggestion chips if available
                if (suggestions && suggestions.length > 0 && messageDiv) {
                    this.renderSuggestionChips(messageDiv, suggestions);
                }
                // Update current channel ID if returned (for new channels)
                if (response.channelId && !this.currentChannelId) {
                    this.currentChannelId = response.channelId;
                    // Save to localStorage
                    localStorage.setItem(UIManager.LAST_CHANNEL_KEY, response.channelId);
                    await this.loadChannels(); // Reload channel list
                    // Update channel selector
                    this.refreshChannelSelect();
                }
                // Update credit badge from response metadata (real-time, no extra API call)
                if (response.credits) {
                    this.creditBalance = {
                        window: {
                            available: response.credits.remaining.window,
                            cap: this.creditBalance?.window?.cap || 1000,
                            used: (this.creditBalance?.window?.cap || 1000) -
                                response.credits.remaining.window,
                            refreshesAt: response.credits.nextWindowRefresh,
                        },
                        bonus: { available: response.credits.remaining.bonus },
                        topup: { available: response.credits.remaining.topup },
                        total: response.credits.remaining.total,
                        plan: this.creditBalance?.plan || 'free',
                    };
                    this.updateCreditIndicator();
                }
                // Reload channel stats from server to get accurate counts
                // (server already persists messageCount and tokens in updateChannelStats)
                if (this.currentChannelId) {
                    await this.loadChannels();
                }
                this.updateUsageIndicator();
            }
            else {
                this.addMessage(`Error: ${response.error || 'Unknown error'}`, 'assistant');
            }
        }
        catch (error) {
            typingIndicator?.classList.remove('show');
            console.error('[UIManager] Error sending chat message:', error);
            this.addMessage('Failed to send message. Please try again.', 'assistant');
        }
    }
    addMessage(text, sender, usage, messageId) {
        const messagesContainer = this.chatPanel?.querySelector('#balancy-messages');
        if (!messagesContainer)
            return null;
        const messageDiv = document.createElement('div');
        messageDiv.className = `balancy-message ${sender === 'assistant' ? 'bot' : 'user'}`;
        // Add message ID as data attribute if available
        if (messageId) {
            messageDiv.setAttribute('data-message-id', messageId);
        }
        const currentTime = new Date().toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
        });
        const tokensHTML = usage?.totalTokens
            ? `<span class="balancy-message-tokens">${this.formatTokens(usage.totalTokens)} tokens</span>`
            : '';
        // Render markdown for assistant messages, escape HTML for user messages
        const messageContent = sender === 'assistant' ? renderMarkdown(text) : this.escapeHTML(text);
        // Add feedback buttons for assistant messages with IDs
        const feedbackHTML = sender === 'assistant' && messageId
            ? this.createFeedbackHTML(messageId)
            : '';
        messageDiv.innerHTML = `
      <div class="balancy-message-content">
        <div class="balancy-message-text">${messageContent}</div>
        <div class="balancy-message-footer">
          <div class="balancy-message-timestamp">${currentTime}</div>
          ${tokensHTML}
        </div>
        ${feedbackHTML}
      </div>
    `;
        messagesContainer.appendChild(messageDiv);
        // Initialize copy buttons for code blocks in this message
        initializeCopyButtons(messageDiv);
        // Initialize feedback handlers if this is an assistant message with feedback UI
        if (sender === 'assistant' && messageId) {
            this.initializeFeedbackHandlers(messageDiv);
        }
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
        // Add to messages array
        this.messages.push({
            id: messageId,
            role: sender,
            content: text,
            timestamp: new Date().toISOString(),
            tokens: usage
                ? {
                    total: usage.totalTokens,
                    prompt: usage.promptTokens,
                    completion: usage.completionTokens,
                }
                : undefined,
        });
        return messageDiv;
    }
    /**
     * Show an inline tool confirmation prompt in the chat panel.
     * Hides the input area and typing indicator, shows a confirmation card
     * with Approve/Reject buttons. Returns the user's decision.
     */
    showToolConfirmation(toolName, description, args) {
        return new Promise((resolve) => {
            const messagesContainer = this.chatPanel?.querySelector('#balancy-messages');
            const inputArea = this.chatPanel?.querySelector('.balancy-chat-input-area');
            const typingIndicator = this.chatPanel?.querySelector('#balancy-typing-indicator');
            if (!messagesContainer) {
                resolve({ approved: false, comment: 'Chat panel not available' });
                return;
            }
            // Hide typing indicator and input area
            typingIndicator?.classList.remove('show');
            if (inputArea)
                inputArea.style.display = 'none';
            // Build argument lines
            const argEntries = Object.entries(args);
            const argLines = argEntries
                .map(([k, v]) => `<span class="balancy-confirm-args-key">${this.escapeHTML(k)}</span> <span class="balancy-confirm-args-value">${this.escapeHTML(typeof v === 'string' ? v : JSON.stringify(v))}</span>`)
                .join('\n');
            // Create confirmation card
            const card = document.createElement('div');
            card.className = 'balancy-message bot balancy-confirm-card';
            card.innerHTML = `
        <div class="balancy-message-content">
          <div class="balancy-confirm-header">
            <div class="balancy-confirm-icon">&#9889;</div>
            <div>
              <div class="balancy-confirm-title">Confirm action</div>
              <div class="balancy-confirm-tool-name">${this.escapeHTML(toolName)}</div>
            </div>
          </div>
          <div class="balancy-confirm-description">${this.escapeHTML(description)}</div>
          ${argLines ? `<pre class="balancy-confirm-args">${argLines}</pre>` : ''}
          <div class="balancy-confirm-actions" id="balancy-confirm-actions">
            <button class="balancy-confirm-btn balancy-confirm-btn-reject" id="balancy-confirm-reject">Reject</button>
            <button class="balancy-confirm-btn balancy-confirm-btn-approve" id="balancy-confirm-approve">Approve</button>
            <input class="balancy-confirm-comment" id="balancy-confirm-comment" type="text" placeholder="Reason (optional)" />
          </div>
        </div>
      `;
            messagesContainer.appendChild(card);
            messagesContainer.scrollTop = messagesContainer.scrollHeight;
            const cleanup = (result) => {
                // Replace actions with result indicator
                const actions = card.querySelector('#balancy-confirm-actions');
                if (actions) {
                    const approved = result.approved;
                    const cls = approved
                        ? 'balancy-confirm-result-approved'
                        : 'balancy-confirm-result-rejected';
                    const icon = approved ? '&#10003;' : '&#10005;';
                    const label = approved ? 'Approved' : 'Rejected';
                    const comment = result.comment
                        ? ` &mdash; ${this.escapeHTML(result.comment)}`
                        : '';
                    actions.innerHTML = `<span class="balancy-confirm-result ${cls}">${icon} ${label}${comment}</span>`;
                }
                // Restore input area and show typing indicator if approved
                // (LLM continues processing after tool execution)
                if (inputArea)
                    inputArea.style.display = '';
                if (result.approved) {
                    typingIndicator?.classList.add('show');
                }
                resolve(result);
            };
            const rejectBtn = card.querySelector('#balancy-confirm-reject');
            const approveBtn = card.querySelector('#balancy-confirm-approve');
            const commentInput = card.querySelector('#balancy-confirm-comment');
            rejectBtn?.addEventListener('click', () => {
                const comment = commentInput?.value.trim() || undefined;
                cleanup({ approved: false, comment });
            });
            approveBtn?.addEventListener('click', () => {
                cleanup({ approved: true });
            });
            // Focus reject button (safe default)
            rejectBtn?.focus();
        });
    }
    /**
     * Set the SDK version string to display in the panel header
     */
    setVersion(version) {
        this.version = version;
        // Update if panel already exists
        const versionEl = this.chatPanel?.querySelector('.balancy-version');
        if (versionEl) {
            versionEl.textContent = `v${version}`;
        }
    }
    setAssetsBaseUrl(url) {
        this.assetsBaseUrl = url;
    }
    setStatus(status) {
        console.log('[UIManager] Status:', status);
    }
    setConnectionStatus(status) {
        // Update panel avatar indicator
        const avatar = this.chatPanel?.querySelector('.balancy-panel-avatar');
        if (avatar) {
            avatar.classList.remove('online', 'offline');
            avatar.classList.add(status === 'connected' ? 'online' : 'offline');
        }
        console.log('[UIManager] Connection status:', status);
    }
    updateGameSyncStatus(status) {
        console.log('[UIManager] Game sync status updated:', status);
    }
    // ── Credit System ──────────────────────────────────────────────
    async loadCreditBalance() {
        try {
            const headers = this.adapter.getAuthHeaders();
            const response = await fetch(`${this.adapter.apiBaseUrl}/api/credits/balance`, { headers });
            if (response.ok) {
                this.creditBalance = await response.json();
                this.updateCreditIndicator();
                this.startCreditCountdown();
            }
        }
        catch (error) {
            console.error('[UIManager] Failed to load token balance:', error);
        }
    }
    startCreditCountdown() {
        if (this.creditCountdownTimer)
            clearInterval(this.creditCountdownTimer);
        if (!this.creditBalance?.window?.refreshesAt)
            return;
        this.creditCountdownTimer = setInterval(() => {
            this.updateCreditCountdown();
        }, 1000);
    }
    updateCreditCountdown() {
        if (!this.creditBalance?.window?.refreshesAt)
            return;
        const resetTime = new Date(this.creditBalance.window.refreshesAt).getTime();
        const now = Date.now();
        const diff = Math.max(0, resetTime - now);
        if (diff === 0) {
            if (this.creditCountdownTimer)
                clearInterval(this.creditCountdownTimer);
            this.loadCreditBalance();
            return;
        }
        const hours = Math.floor(diff / 3600000);
        const minutes = Math.floor((diff % 3600000) / 60000);
        const seconds = Math.floor((diff % 60000) / 1000);
        const timeStr = `${hours}h ${minutes.toString().padStart(2, '0')}m ${seconds.toString().padStart(2, '0')}s`;
        const countdownEl = this.chatPanel?.querySelector('#balancy-credit-countdown');
        if (countdownEl)
            countdownEl.textContent = timeStr;
    }
    updateCreditIndicator() {
        const badge = this.chatPanel?.querySelector('#balancy-credit-badge');
        if (!badge || !this.creditBalance)
            return;
        const { total, window: win } = this.creditBalance;
        const percentage = win.cap > 0 ? total / win.cap : 0;
        let level = 'green';
        if (percentage <= 0.1)
            level = 'red';
        else if (percentage <= 0.5)
            level = 'yellow';
        badge.className = `balancy-credit-badge balancy-credit-${level}`;
        const valueEl = badge.querySelector('.balancy-credit-value');
        if (valueEl)
            valueEl.textContent = total.toLocaleString();
    }
    showCreditsView() {
        this.currentView = 'credits';
        let creditsView = this.chatPanel?.querySelector('#balancy-credits-view');
        if (!creditsView) {
            creditsView = document.createElement('div');
            creditsView.id = 'balancy-credits-view';
            creditsView.className = 'balancy-credits-view';
            // Event delegation — set up once on creation
            creditsView.addEventListener('click', (e) => {
                const target = e.target;
                if (target.closest('#balancy-credits-back')) {
                    e.stopPropagation();
                    this.hideCreditsView();
                }
                else if (target.closest('#balancy-credits-activate')) {
                    e.stopPropagation();
                    this.activateOnboarding();
                }
                else if (target.closest('.balancy-credits-hint-icon')) {
                    e.stopPropagation();
                    const hint = target
                        .closest('.balancy-credits-row')
                        ?.querySelector('.balancy-credits-hint-text');
                    if (hint) {
                        const isVisible = hint.style.getPropertyValue('display') === 'block';
                        // Close all other hints first
                        e.currentTarget
                            .querySelectorAll('.balancy-credits-hint-text')
                            .forEach((el) => {
                            el.style.setProperty('display', 'none', 'important');
                        });
                        if (!isVisible) {
                            hint.style.setProperty('display', 'block', 'important');
                        }
                    }
                }
            });
            this.chatPanel?.appendChild(creditsView);
        }
        creditsView.style.setProperty('display', 'flex', 'important');
        this.renderCreditsContent(creditsView);
    }
    hideCreditsView() {
        this.currentView = 'chat';
        const creditsView = this.chatPanel?.querySelector('#balancy-credits-view');
        if (creditsView)
            creditsView.style.setProperty('display', 'none', 'important');
    }
    // -------------------------------------------------------------------------
    // MCP Bridge View
    // -------------------------------------------------------------------------
    showMCPView() {
        this.currentView = 'mcp';
        // Hide credits if open
        const creditsView = this.chatPanel?.querySelector('#balancy-credits-view');
        if (creditsView)
            creditsView.style.setProperty('display', 'none', 'important');
        let mcpView = this.chatPanel?.querySelector('#balancy-mcp-view');
        if (!mcpView) {
            mcpView = document.createElement('div');
            mcpView.id = 'balancy-mcp-view';
            mcpView.className = 'balancy-mcp-view';
            const viewEl = mcpView; // capture non-null reference for closure
            mcpView.addEventListener('click', (e) => {
                const target = e.target;
                if (target.closest('#balancy-mcp-back')) {
                    e.stopPropagation();
                    this.hideMCPView();
                }
                else if (target.closest('#balancy-mcp-connect')) {
                    e.stopPropagation();
                    this.onMCPConnect?.();
                }
                else if (target.closest('#balancy-mcp-disconnect')) {
                    e.stopPropagation();
                    this.onMCPDisconnect?.();
                }
                else if (target.closest('#balancy-apikey-create-submit')) {
                    e.stopPropagation();
                    this.handleCreateApiKey(viewEl);
                }
                else if (target.closest('#balancy-apikey-copy')) {
                    e.stopPropagation();
                    this.handleCopyCreatedKey(viewEl);
                }
                else if (target.closest('#balancy-apikey-done')) {
                    e.stopPropagation();
                    this.newlyCreatedKey = null;
                    this.renderMCPContent(viewEl);
                }
                else if (target.closest('.balancy-apikey-revoke')) {
                    e.stopPropagation();
                    const btn = target.closest('.balancy-apikey-revoke');
                    const keyId = btn.getAttribute('data-key-id');
                    if (keyId)
                        this.handleRevokeApiKey(keyId, viewEl);
                }
                else if (target.closest('.balancy-apikey-copy-prefix')) {
                    e.stopPropagation();
                    const btn = target.closest('.balancy-apikey-copy-prefix');
                    const prefix = btn.getAttribute('data-prefix') || '';
                    navigator.clipboard.writeText(prefix).catch(() => { });
                }
                else if (target.closest('.balancy-mcp-copy-cmd')) {
                    e.stopPropagation();
                    const btn = target.closest('.balancy-mcp-copy-cmd');
                    const encoded = btn.getAttribute('data-copy') || '';
                    navigator.clipboard
                        .writeText(decodeURIComponent(encoded))
                        .catch(() => { });
                    const original = btn.textContent;
                    btn.textContent = 'Copied';
                    window.setTimeout(() => {
                        btn.textContent = original;
                    }, 1200);
                }
            });
            this.chatPanel?.appendChild(mcpView);
        }
        // Inline critical layout styles so the overlay works even if CSS
        // hasn't been updated on the CDN yet.
        mcpView.style.setProperty('display', 'flex', 'important');
        mcpView.style.setProperty('flex-direction', 'column', 'important');
        mcpView.style.setProperty('position', 'absolute', 'important');
        mcpView.style.setProperty('top', '57px', 'important');
        mcpView.style.setProperty('left', '0', 'important');
        mcpView.style.setProperty('right', '0', 'important');
        mcpView.style.setProperty('bottom', '0', 'important');
        mcpView.style.setProperty('padding', '16px', 'important');
        mcpView.style.setProperty('overflow-y', 'auto', 'important');
        mcpView.style.setProperty('background', 'rgba(15, 15, 15, 0.98)', 'important');
        mcpView.style.setProperty('z-index', '2000', 'important');
        this.renderMCPContent(mcpView);
        // Load API keys if not yet loaded
        if (!this.apiKeysLoaded) {
            this.loadApiKeys(mcpView);
        }
    }
    hideMCPView() {
        this.currentView = 'chat';
        const mcpView = this.chatPanel?.querySelector('#balancy-mcp-view');
        if (mcpView)
            mcpView.style.setProperty('display', 'none', 'important');
    }
    renderMCPContent(container) {
        const statusLabel = this.mcpStatus === 'connected'
            ? 'Connected'
            : this.mcpStatus === 'connecting'
                ? 'Connecting\u2026'
                : this.mcpStatus === 'replaced'
                    ? 'Active in another tab'
                    : this.mcpStatus === 'auth_failed'
                        ? 'Authentication failed'
                        : 'Disconnected';
        // Only add a detail line where the status card alone doesn't tell the user
        // what to do. "Authentication failed" is the one such case — the bridge
        // can't refresh its own token, so recovery is a page reload.
        const detailParts = [];
        if (this.mcpStatus === 'auth_failed') {
            detailParts.push('If reconnecting fails, reload the page');
        }
        const detailHtml = detailParts.length > 0
            ? `<div class="balancy-mcp-status-detail">${this.escapeHTML(detailParts.join(' \u00b7 '))}</div>`
            : '';
        const activityHtml = this.mcpActivities.length > 0
            ? this.mcpActivities
                .slice()
                .reverse()
                .map((a) => {
                const icon = a.status === 'completed'
                    ? '&#10003;'
                    : a.status === 'failed'
                        ? '&#10007;'
                        : '&#8943;';
                const ago = this.timeAgo(a.timestamp);
                return `<div class="balancy-mcp-activity-item">
                <span class="balancy-mcp-activity-icon ${a.status}">${icon}</span>
                <span class="balancy-mcp-activity-name">${this.escapeHTML(a.toolName)}</span>
                <span class="balancy-mcp-activity-time">${ago}</span>
              </div>`;
            })
                .join('')
            : '<div class="balancy-mcp-activity-empty">No activity yet</div>';
        const connectBtn = this.mcpStatus === 'connected'
            ? '<button id="balancy-mcp-disconnect" class="balancy-mcp-disconnect-btn">Disconnect</button>'
            : `<button id="balancy-mcp-connect" class="balancy-mcp-connect-btn"${this.mcpStatus === 'connecting' ? ' disabled' : ''}>${this.mcpStatus === 'replaced' ? 'Use here' : 'Connect to External AI'}</button>`;
        container.innerHTML = `
      <div class="balancy-mcp-header">
        <button id="balancy-mcp-back" class="balancy-mcp-back">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
          Back to Chat
        </button>
        <span class="balancy-mcp-title">External AI Bridge</span>
      </div>

      <div class="balancy-mcp-status-card">
        <div class="balancy-mcp-status-row">
          <span class="balancy-mcp-status-dot ${this.mcpStatus}"></span>
          <span class="balancy-mcp-status-label">${statusLabel}</span>
          ${this.mcpStatus === 'connected' ? '<button id="balancy-mcp-disconnect" class="balancy-mcp-disconnect-btn">Disconnect</button>' : ''}
        </div>
        ${detailHtml}
      </div>

      ${this.mcpErrorMessage ? `<div class="balancy-mcp-error">${this.escapeHTML(this.mcpErrorMessage)}</div>` : ''}

      ${this.mcpStatus !== 'connected' ? connectBtn : ''}

      <div class="balancy-mcp-section">
        <div class="balancy-mcp-section-title">Recent Activity</div>
        ${activityHtml}
      </div>

      <div class="balancy-mcp-section">
        <div class="balancy-mcp-section-title">API Keys</div>
        ${this.renderApiKeysSection()}
      </div>

      <div class="balancy-mcp-section">
        ${this.renderCliInstructions(null)}
      </div>

      <div class="balancy-mcp-footer">
        Use Claude Desktop, Cursor, or any MCP client to connect your own LLM.
        Tools execute locally in this browser tab.
      </div>
    `;
    }
    getMcpSseEndpoint() {
        const apiBaseUrl = this.adapter.apiBaseUrl || this.adapter.getApiBaseUrl();
        return `${String(apiBaseUrl).replace(/\/$/, '')}/mcp/sse`;
    }
    /**
     * Compact CLI setup instructions (Claude Code / Codex) for the MCP popup.
     * When `apiKey` is provided the snippets embed the real key and the block is
     * expanded; otherwise a `YOUR_API_KEY` placeholder is used.
     */
    renderCliInstructions(apiKey) {
        const sse = this.getMcpSseEndpoint();
        const key = apiKey || 'YOUR_API_KEY';
        const claudeCodeCli = `claude mcp add --transport sse balancy \\\n  ${sse} \\\n  --header "Authorization: Bearer ${key}"`;
        const claudeCodeJson = JSON.stringify({
            mcpServers: {
                balancy: {
                    type: 'sse',
                    url: sse,
                    headers: { Authorization: `Bearer ${key}` },
                },
            },
        }, null, 2);
        const codexToml = `[mcp_servers.balancy]\ncommand = "npx"\nargs = ["-y", "mcp-remote", "${sse}", "--header", "Authorization: Bearer ${key}"]`;
        const block = (title, code) => `
      <div style="margin-top:10px;">
        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:4px;gap:8px;">
          <span style="font-size:11px;color:#9ca3af;">${this.escapeHTML(title)}</span>
          <button class="balancy-mcp-copy-cmd" data-copy="${encodeURIComponent(code)}" style="flex:none;font-size:11px;padding:2px 8px;background:#2563eb;color:#fff;border:none;border-radius:4px;cursor:pointer;">Copy</button>
        </div>
        <pre style="margin:0;padding:8px;background:#0a0a0a;color:#22c55e;border:1px solid #262626;border-radius:6px;font-size:11px;line-height:1.5;white-space:pre-wrap;word-break:break-word;overflow-x:auto;">${this.escapeHTML(code)}</pre>
      </div>`;
        const note = apiKey
            ? 'Ready to paste \u2014 the real key is included below. It won\u2019t be shown again.'
            : 'Create an API key above (shown once), then replace <code>YOUR_API_KEY</code> in the snippets below.';
        return `
      <details${apiKey ? ' open' : ''} style="margin-top:4px;">
        <summary style="cursor:pointer;font-size:13px;font-weight:600;color:#e5e7eb;list-style:none;">Connect Claude Code / Codex</summary>
        <div style="margin-top:8px;">
          <div style="font-size:11px;color:#9ca3af;line-height:1.5;">${note}</div>
          ${block('Claude Code \u2014 one command', claudeCodeCli)}
          ${block('Claude Code \u2014 .mcp.json', claudeCodeJson)}
          ${block('Codex \u2014 ~/.codex/config.toml (needs mcp-remote)', codexToml)}
        </div>
      </details>`;
    }
    // -------------------------------------------------------------------------
    // API Keys Management
    // -------------------------------------------------------------------------
    renderApiKeysSection() {
        // Show newly created key
        if (this.newlyCreatedKey) {
            return `
        <div class="balancy-mcp-key-created">
          <div class="balancy-mcp-key-created-label">Key created \u2014 copy it now. It won\u2019t be shown again.</div>
          <div class="balancy-mcp-key-created-row">
            <code class="balancy-mcp-key-created-value">${this.escapeHTML(this.newlyCreatedKey)}</code>
            <button id="balancy-apikey-copy" class="balancy-mcp-key-copy-btn">Copy</button>
          </div>
          ${this.renderCliInstructions(this.newlyCreatedKey)}
          <button id="balancy-apikey-done" class="balancy-mcp-key-done-btn">Done</button>
        </div>`;
        }
        // Create key form
        const createForm = `
      <div class="balancy-mcp-key-form">
        <input id="balancy-apikey-name" type="text" placeholder="Key name (e.g. Claude Desktop)"
          class="balancy-mcp-key-input" />
        <button id="balancy-apikey-create-submit" class="balancy-mcp-key-create-btn">Create</button>
      </div>`;
        if (!this.apiKeysLoaded) {
            return (createForm + '<div class="balancy-mcp-key-loading">Loading\u2026</div>');
        }
        const activeKeys = this.apiKeys.filter((k) => k.isActive);
        if (activeKeys.length === 0) {
            return (createForm +
                '<div class="balancy-mcp-key-empty">No API keys yet. Create one to connect external clients.</div>');
        }
        const keysHtml = activeKeys
            .map((k) => `
        <div class="balancy-mcp-key-item">
          <div class="balancy-mcp-key-info">
            <div class="balancy-mcp-key-name">${this.escapeHTML(k.name || 'Unnamed')}</div>
            <div class="balancy-mcp-key-prefix balancy-apikey-copy-prefix" data-prefix="${this.escapeHTML(k.prefix)}">${this.escapeHTML(k.prefix)}\u2026</div>
          </div>
          <div class="balancy-mcp-key-used">${k.lastUsedAt ? this.timeAgo(new Date(k.lastUsedAt).getTime()) : 'Never used'}</div>
          <button class="balancy-apikey-revoke balancy-mcp-key-revoke" data-key-id="${k.id}">Revoke</button>
        </div>`)
            .join('');
        return createForm + keysHtml;
    }
    async loadApiKeys(container) {
        try {
            const headers = this.adapter.getAuthHeaders();
            const apiBaseUrl = this.adapter.apiBaseUrl || this.adapter.getApiBaseUrl();
            const response = await fetch(`${apiBaseUrl}/api/keys`, { headers });
            if (!response.ok)
                return;
            const data = await response.json();
            if (data.success && data.data?.keys) {
                this.apiKeys = data.data.keys;
                this.apiKeysLoaded = true;
                if (this.currentView === 'mcp') {
                    this.renderMCPContent(container);
                }
            }
        }
        catch (err) {
            console.error('[UIManager] Failed to load API keys:', err);
        }
    }
    async handleCreateApiKey(container) {
        const input = container.querySelector('#balancy-apikey-name');
        const name = input?.value?.trim();
        if (!name)
            return;
        try {
            const headers = this.adapter.getAuthHeaders();
            const apiBaseUrl = this.adapter.apiBaseUrl || this.adapter.getApiBaseUrl();
            const response = await fetch(`${apiBaseUrl}/api/keys`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ name }),
            });
            const data = await response.json();
            if (data.success && data.data?.key) {
                this.newlyCreatedKey = data.data.key;
                // Refresh key list
                this.apiKeysLoaded = false;
                this.loadApiKeys(container);
                this.renderMCPContent(container);
            }
        }
        catch (err) {
            console.error('[UIManager] Failed to create API key:', err);
        }
    }
    async handleRevokeApiKey(keyId, container) {
        try {
            const headers = this.adapter.getAuthHeaders();
            const apiBaseUrl = this.adapter.apiBaseUrl || this.adapter.getApiBaseUrl();
            const response = await fetch(`${apiBaseUrl}/api/keys/${keyId}`, {
                method: 'DELETE',
                headers,
            });
            const data = await response.json();
            if (data.success) {
                this.apiKeysLoaded = false;
                this.loadApiKeys(container);
            }
        }
        catch (err) {
            console.error('[UIManager] Failed to revoke API key:', err);
        }
    }
    handleCopyCreatedKey(container) {
        if (!this.newlyCreatedKey)
            return;
        navigator.clipboard
            .writeText(this.newlyCreatedKey)
            .then(() => {
            const btn = container.querySelector('#balancy-apikey-copy');
            if (btn) {
                btn.textContent = 'Copied!';
                setTimeout(() => {
                    if (btn)
                        btn.textContent = 'Copy';
                }, 2000);
            }
        })
            .catch(() => { });
    }
    timeAgo(ts) {
        const seconds = Math.floor((Date.now() - ts) / 1000);
        if (seconds < 5)
            return 'now';
        if (seconds < 60)
            return `${seconds}s ago`;
        const minutes = Math.floor(seconds / 60);
        if (minutes < 60)
            return `${minutes}m ago`;
        return `${Math.floor(minutes / 60)}h ago`;
    }
    // -------------------------------------------------------------------------
    // MCP Public API
    // -------------------------------------------------------------------------
    /**
     * Set MCP bridge connection handlers.
     */
    setMCPHandlers(onConnect, onDisconnect) {
        this.onMCPConnect = onConnect;
        this.onMCPDisconnect = onDisconnect;
    }
    /**
     * Update MCP bridge status (called by MCPBridgeManager).
     */
    setMCPStatus(status) {
        this.mcpStatus = status;
        this.mcpErrorMessage = null; // Clear error on status change
        // Update button appearance
        const mcpBtns = this.chatPanel?.querySelectorAll('.balancy-mcp-btn');
        mcpBtns?.forEach((btn) => {
            if (status === 'connected') {
                btn.classList.add('active');
                btn.setAttribute('title', 'External AI (Connected)');
            }
            else {
                btn.classList.remove('active');
                btn.setAttribute('title', 'External AI');
            }
        });
        // Update floating icon MCP dot
        const mcpDot = this.floatingIcon?.querySelector('#balancy-mcp-dot');
        if (mcpDot) {
            if (status === 'connected') {
                mcpDot.className = 'balancy-mcp-dot balancy-mcp-connected';
            }
            else if (status === 'connecting') {
                mcpDot.className = 'balancy-mcp-dot balancy-mcp-connecting';
            }
            else {
                mcpDot.className = 'balancy-mcp-dot';
            }
        }
        // Re-render MCP view if open
        if (this.currentView === 'mcp') {
            const mcpView = this.chatPanel?.querySelector('#balancy-mcp-view');
            if (mcpView)
                this.renderMCPContent(mcpView);
        }
    }
    /**
     * Add MCP tool activity (called by MCPBridgeManager).
     */
    /**
     * Show an error message in the MCP view. Cleared on next status change.
     */
    setMCPError(message) {
        this.mcpErrorMessage = message;
        if (this.currentView === 'mcp') {
            const mcpView = this.chatPanel?.querySelector('#balancy-mcp-view');
            if (mcpView)
                this.renderMCPContent(mcpView);
        }
    }
    addMCPActivity(activity) {
        this.mcpActivities.push(activity);
        if (this.mcpActivities.length > 20) {
            this.mcpActivities = this.mcpActivities.slice(-20);
        }
        // Re-render MCP view if open
        if (this.currentView === 'mcp') {
            const mcpView = this.chatPanel?.querySelector('#balancy-mcp-view');
            if (mcpView)
                this.renderMCPContent(mcpView);
        }
    }
    renderCreditsContent(container) {
        if (!this.creditBalance) {
            container.innerHTML =
                '<div class="balancy-credits-loading">Loading tokens...</div>';
            return;
        }
        const { window: win, bonus, topup, total, plan } = this.creditBalance;
        const windowCredits = win.available;
        const windowMax = win.cap;
        const bonusCredits = bonus.available;
        const topUpCredits = topup.available;
        const percentage = windowMax > 0 ? Math.min((total / windowMax) * 100, 100) : 0;
        let level = 'green';
        if (percentage <= 10)
            level = 'red';
        else if (percentage <= 50)
            level = 'yellow';
        const hintIcon = `<svg class="balancy-credits-hint-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>`;
        container.innerHTML = `
      <div class="balancy-credits-header">
        <button class="balancy-credits-back" id="balancy-credits-back">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
          Back to Chat
        </button>
        <span class="balancy-credits-plan">${this.escapeHTML(plan || 'free')}</span>
      </div>

      <div class="balancy-credits-balance-card">
        <div class="balancy-credits-total">
          <span class="balancy-credits-total-value">${total.toLocaleString()}</span>
          <span class="balancy-credits-total-label">tokens available</span>
        </div>
        <div class="balancy-credits-progress-bar">
          <div class="balancy-credits-progress-fill balancy-credit-${level}" style="width: ${percentage}%"></div>
        </div>
        <div class="balancy-credits-countdown-row">
          <span class="balancy-credits-countdown-label">Window resets in:</span>
          <span class="balancy-credits-countdown-value" id="balancy-credit-countdown">--:--:--</span>
        </div>
      </div>

      <div class="balancy-credits-breakdown">
        <div class="balancy-credits-row">
          <span class="balancy-credits-row-label">Window tokens ${hintIcon}</span>
          <span class="balancy-credits-row-value">${windowCredits.toLocaleString()} / ${windowMax.toLocaleString()}</span>
          <div class="balancy-credits-hint-text">Tokens that automatically replenish every 6 hours. Your base allowance based on your plan.</div>
        </div>
        <div class="balancy-credits-row">
          <span class="balancy-credits-row-label">Bonus tokens ${hintIcon}</span>
          <span class="balancy-credits-row-value">${bonusCredits.toLocaleString()}</span>
          <div class="balancy-credits-hint-text">One-time tokens from promotions or onboarding. Used after window tokens run out. Do not replenish.</div>
        </div>
        <div class="balancy-credits-row">
          <span class="balancy-credits-row-label">Top-up tokens ${hintIcon}</span>
          <span class="balancy-credits-row-value">${topUpCredits.toLocaleString()}</span>
          <div class="balancy-credits-hint-text">Purchased tokens ($1 = 1,000 tokens). Used last, after window and bonus tokens are exhausted. Do not expire.</div>
        </div>
      </div>

      ${!this.creditBalance?.onboardingActivated
            ? `
      <button class="balancy-credits-activate-btn" id="balancy-credits-activate">
        Activate Onboarding Bonus (10,000 tokens)
      </button>`
            : ''}
    `;
        // Start countdown display
        this.updateCreditCountdown();
    }
    async activateOnboarding() {
        try {
            const headers = this.adapter.getAuthHeaders();
            const activateBtn = this.chatPanel?.querySelector('#balancy-credits-activate');
            if (activateBtn) {
                activateBtn.disabled = true;
                activateBtn.textContent = 'Activating...';
            }
            const response = await fetch(`${this.adapter.apiBaseUrl}/api/credits/activate-onboarding`, {
                method: 'POST',
                headers,
            });
            if (response.ok) {
                await this.loadCreditBalance();
                // Re-render credits view
                const creditsView = this.chatPanel?.querySelector('#balancy-credits-view');
                if (creditsView)
                    this.renderCreditsContent(creditsView);
            }
            else {
                const data = await response.json();
                if (activateBtn) {
                    activateBtn.textContent = data.error?.message || 'Already activated';
                    setTimeout(() => {
                        activateBtn.textContent = 'Activate Onboarding Bonus';
                        activateBtn.disabled = false;
                    }, 3000);
                }
            }
        }
        catch (error) {
            console.error('[UIManager] Failed to activate onboarding:', error);
        }
    }
    // ---------------------------------------------------------------------------
    // Analytics overlay
    // ---------------------------------------------------------------------------
    /**
     * Load a script into the document head.
     * Returns a promise that resolves when the script is loaded.
     */
    loadScript(src) {
        return new Promise((resolve, reject) => {
            if (document.querySelector(`script[src="${src}"]`)) {
                resolve();
                return;
            }
            const script = document.createElement('script');
            script.src = src;
            script.onload = () => resolve();
            script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
            document.head.appendChild(script);
        });
    }
    /**
     * Show the analytics dashboard as a full-screen overlay inside Shadow DOM.
     * Lazily loads the IIFE bundle if not already loaded.
     */
    async showAnalytics(cdnBaseUrl) {
        if (this.analyticsContainer) {
            // Already showing
            return;
        }
        // Ensure shadow DOM is ready
        this.initShadowDOM();
        if (!this.shadowRoot)
            return;
        // Detect host theme for consistent styling
        const hostTheme = this.detectHostTheme();
        const isDark = hostTheme === 'dark';
        // Create a full-screen container inside shadow DOM
        this.analyticsContainer = document.createElement('div');
        Object.assign(this.analyticsContainer.style, {
            position: 'fixed',
            top: '0',
            left: '0',
            width: '100vw',
            height: '100vh',
            zIndex: '999998',
            pointerEvents: 'auto',
            background: isDark ? '#0f0f0f' : '#ffffff',
            overflow: 'auto',
        });
        this.shadowRoot.appendChild(this.analyticsContainer);
        // Load the IIFE script if not already loaded
        if (!this.analyticsScriptLoaded) {
            const scriptUrl = `${cdnBaseUrl}/balancy-analytics.iife.js`;
            try {
                await this.loadScript(scriptUrl);
                this.analyticsScriptLoaded = true;
            }
            catch (err) {
                console.error('[UIManager] Failed to load analytics bundle:', err);
                this.analyticsContainer.remove();
                this.analyticsContainer = null;
                return;
            }
        }
        // Call BalancyAnalytics.mount()
        const BA = window.BalancyAnalytics;
        if (!BA || typeof BA.mount !== 'function') {
            console.error('[UIManager] BalancyAnalytics.mount not available');
            this.analyticsContainer.remove();
            this.analyticsContainer = null;
            return;
        }
        const apiBaseUrl = this.adapter.getApiBaseUrl() + '/api';
        const token = await this.adapter.getAccessToken();
        const user = this.adapter.getUser();
        const context = this.adapter.getContext();
        this.analyticsUnmount = BA.mount(this.analyticsContainer, {
            apiBaseUrl,
            authToken: token || undefined,
            userEmail: user?.email,
            onClose: () => this.hideAnalytics(),
            initialGameId: context?.apiGameId || undefined,
            forceDark: isDark,
        });
        console.log(`[UIManager] Analytics overlay shown (theme: ${hostTheme})`);
    }
    /**
     * Hide the analytics dashboard overlay.
     */
    hideAnalytics() {
        if (this.analyticsUnmount) {
            try {
                this.analyticsUnmount();
            }
            catch (err) {
                console.error('[UIManager] Error unmounting analytics:', err);
            }
            this.analyticsUnmount = null;
        }
        if (this.analyticsContainer) {
            this.analyticsContainer.remove();
            this.analyticsContainer = null;
        }
        console.log('[UIManager] Analytics overlay hidden');
    }
    /**
     * Mount the analytics dashboard inline into an existing host-page element.
     * Unlike showAnalytics(), this does NOT create an overlay — it renders
     * directly into the provided DOM element.
     */
    async mountAnalyticsInline(hostElement, cdnBaseUrl, gameId) {
        // Already mounted into this element
        if (this.analyticsInlineHost === hostElement &&
            this.analyticsInlineUnmount) {
            return;
        }
        // If a mount is already in progress, wait for it to finish then bail
        // (the in-flight mount will produce the same result)
        if (this.analyticsInlineMounting) {
            await this.analyticsInlineMounting;
            return;
        }
        this.analyticsInlineMounting = this.doMountAnalyticsInline(hostElement, cdnBaseUrl, gameId);
        try {
            await this.analyticsInlineMounting;
        }
        finally {
            this.analyticsInlineMounting = null;
        }
    }
    async doMountAnalyticsInline(hostElement, cdnBaseUrl, gameId) {
        // Clean up previous inline mount if any
        this.unmountAnalyticsInline();
        // Load the IIFE script if not already loaded
        if (!this.analyticsScriptLoaded) {
            const scriptUrl = `${cdnBaseUrl}/balancy-analytics.iife.js`;
            try {
                await this.loadScript(scriptUrl);
                this.analyticsScriptLoaded = true;
            }
            catch (err) {
                console.error('[UIManager] Failed to load analytics bundle:', err);
                return;
            }
        }
        const BA = window.BalancyAnalytics;
        if (!BA || typeof BA.mount !== 'function') {
            console.error('[UIManager] BalancyAnalytics.mount not available');
            return;
        }
        const apiBaseUrl = this.adapter.getApiBaseUrl() + '/api';
        const token = await this.adapter.getAccessToken();
        const user = this.adapter.getUser();
        // Clear the host element's existing children (e.g. the host page's loading spinner)
        // so our dashboard is the only visible content.
        const existingChildren = Array.from(hostElement.children);
        existingChildren.forEach((child) => child.remove());
        // Detect host page theme (Chakra UI uses data-theme on <html>)
        const hostTheme = this.detectHostTheme();
        const isDark = hostTheme === 'dark';
        this.analyticsInlineHost = hostElement;
        this.analyticsInlineUnmount = BA.mount(hostElement, {
            apiBaseUrl,
            authToken: token || undefined,
            userEmail: user?.email,
            initialGameId: gameId,
            forceDark: isDark,
        });
        // Store the mount point so we can toggle dark class on theme changes
        // (mount() creates a div.balancy-analytics inside the host element)
        this.analyticsMountPoint =
            hostElement.querySelector('.balancy-analytics') || null;
        // Watch for host theme changes via MutationObserver on <html> data-theme
        this.setupThemeObserver();
        console.log(`[UIManager] Analytics mounted inline (theme: ${hostTheme})`);
    }
    /**
     * Detect the host page's current theme from Chakra UI's data-theme attribute
     * on <html>, falling back to localStorage.
     */
    detectHostTheme() {
        const dataTheme = document.documentElement.getAttribute('data-theme');
        if (dataTheme === 'dark' || dataTheme === 'light')
            return dataTheme;
        try {
            const stored = localStorage.getItem('chakra-ui-color-mode');
            if (stored === 'dark' || stored === 'light')
                return stored;
        }
        catch {
            // localStorage may be unavailable
        }
        // Fallback to system preference
        return window.matchMedia('(prefers-color-scheme: dark)').matches
            ? 'dark'
            : 'light';
    }
    /**
     * Watch <html data-theme> for changes and toggle .dark on the analytics mount point.
     */
    setupThemeObserver() {
        // Clean up any previous observer
        this.teardownThemeObserver();
        this.analyticsThemeObserver = new MutationObserver(() => {
            const theme = this.detectHostTheme();
            if (this.analyticsMountPoint) {
                if (theme === 'dark') {
                    this.analyticsMountPoint.classList.add('dark');
                }
                else {
                    this.analyticsMountPoint.classList.remove('dark');
                }
            }
        });
        this.analyticsThemeObserver.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['data-theme'],
        });
    }
    teardownThemeObserver() {
        if (this.analyticsThemeObserver) {
            this.analyticsThemeObserver.disconnect();
            this.analyticsThemeObserver = null;
        }
    }
    /**
     * Unmount the inline analytics dashboard from the host page element.
     */
    unmountAnalyticsInline() {
        this.teardownThemeObserver();
        this.analyticsMountPoint = null;
        if (this.analyticsInlineUnmount) {
            try {
                this.analyticsInlineUnmount();
            }
            catch (err) {
                console.error('[UIManager] Error unmounting inline analytics:', err);
            }
            this.analyticsInlineUnmount = null;
        }
        this.analyticsInlineHost = null;
    }
    // ── Execution Plan Progress UI ──
    /**
     * Handle progress events from the SSE stream (execution_plan, step_update, plan_complete, tool_progress).
     */
    handleProgressEvent(event) {
        console.log(`[UIManager] Progress event: ${event.type}`, event.data);
        switch (event.type) {
            case 'execution_plan': {
                const steps = event.data.steps;
                if (steps && steps.length > 0) {
                    this.renderPlanChecklist(steps);
                }
                break;
            }
            case 'step_update': {
                const { stepId, status, toolName } = event.data;
                this.updatePlanStep(stepId, status, toolName);
                break;
            }
            case 'step_added': {
                const step = event.data.step;
                if (step) {
                    this.addPlanStep(step);
                }
                break;
            }
            case 'plan_complete':
                this.collapsePlan();
                break;
            case 'tool_progress': {
                const { toolName, status } = event.data;
                // No plan — just update typing indicator with tool info
                if (!this.currentPlanElement) {
                    this.setTypingText(this.humanizeToolName(toolName, status));
                }
                break;
            }
        }
    }
    /**
     * Render a plan checklist block inside the chat messages area.
     */
    renderPlanChecklist(steps) {
        // Reset state
        this.currentPlanSteps = steps.map((s) => ({ ...s, status: 'pending' }));
        const messagesContainer = this.chatPanel?.querySelector('#balancy-messages');
        console.log(`[UIManager] renderPlanChecklist: ${steps.length} steps, messagesContainer=${!!messagesContainer}`);
        if (!messagesContainer)
            return;
        const planBlock = document.createElement('div');
        planBlock.className = 'balancy-plan-block';
        planBlock.innerHTML = `
      <div class="balancy-plan-header">
        <span class="balancy-plan-header-icon">📋</span>
        <span class="balancy-plan-title">Execution Plan</span>
        <span class="balancy-plan-counter">${steps.length} steps</span>
        <button class="balancy-plan-toggle" aria-label="Toggle plan">▼</button>
      </div>
      <div class="balancy-plan-content">
        <div class="balancy-plan-steps">
          ${steps
            .map((s, i) => `
            <div class="balancy-plan-step${i === steps.length - 1 ? ' last' : ''}" data-step-id="${s.id}">
              <div class="balancy-plan-step-track">
                <span class="balancy-plan-step-icon" data-status="pending"></span>
                ${i < steps.length - 1 ? '<span class="balancy-plan-step-line"></span>' : ''}
              </div>
              <span class="balancy-plan-step-label">${this.escapeHTML(s.label)}</span>
            </div>
          `)
            .join('')}
        </div>
      </div>
    `;
        // Wire toggle button
        const toggleBtn = planBlock.querySelector('.balancy-plan-toggle');
        const content = planBlock.querySelector('.balancy-plan-content');
        if (toggleBtn && content) {
            toggleBtn.addEventListener('click', () => {
                const isCollapsed = content.style.display === 'none';
                content.style.display = isCollapsed ? '' : 'none';
                toggleBtn.textContent = isCollapsed ? '▼' : '▶';
            });
        }
        messagesContainer.appendChild(planBlock);
        this.currentPlanElement = planBlock;
        // Scroll plan block into view
        requestAnimationFrame(() => {
            planBlock.scrollIntoView({ behavior: 'smooth', block: 'end' });
        });
    }
    /**
     * Dynamically add a new step to the existing plan checklist.
     */
    addPlanStep(step) {
        if (!this.currentPlanElement)
            return;
        this.currentPlanSteps.push({ ...step, status: 'pending' });
        const stepsContainer = this.currentPlanElement.querySelector('.balancy-plan-steps');
        if (!stepsContainer)
            return;
        // Add connector line to previous last step
        const prevLast = stepsContainer.querySelector('.balancy-plan-step.last');
        if (prevLast) {
            prevLast.classList.remove('last');
            const prevTrack = prevLast.querySelector('.balancy-plan-step-track');
            if (prevTrack && !prevTrack.querySelector('.balancy-plan-step-line')) {
                const line = document.createElement('span');
                line.className = 'balancy-plan-step-line';
                prevTrack.appendChild(line);
            }
        }
        const stepEl = document.createElement('div');
        stepEl.className = 'balancy-plan-step last';
        stepEl.setAttribute('data-step-id', step.id);
        stepEl.innerHTML = `
      <div class="balancy-plan-step-track">
        <span class="balancy-plan-step-icon" data-status="pending"></span>
      </div>
      <span class="balancy-plan-step-label">${this.escapeHTML(step.label)}</span>
    `;
        stepsContainer.appendChild(stepEl);
        // Update counter
        const counter = this.currentPlanElement.querySelector('.balancy-plan-counter');
        if (counter) {
            counter.textContent = `${this.currentPlanSteps.length} steps`;
        }
        // Scroll plan into view
        if (this.currentPlanElement) {
            requestAnimationFrame(() => {
                this.currentPlanElement?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'end',
                });
            });
        }
    }
    /**
     * Update a plan step's status icon and the typing indicator text.
     */
    updatePlanStep(stepId, status, _toolName) {
        if (!this.currentPlanElement)
            return;
        const stepEl = this.currentPlanElement.querySelector(`[data-step-id="${stepId}"]`);
        if (!stepEl)
            return;
        const iconEl = stepEl.querySelector('.balancy-plan-step-icon');
        const labelEl = stepEl.querySelector('.balancy-plan-step-label');
        if (iconEl) {
            iconEl.setAttribute('data-status', status);
        }
        // Also update the connector line color for completed steps
        const lineEl = stepEl.querySelector('.balancy-plan-step-line');
        if (lineEl && status === 'completed') {
            lineEl.setAttribute('data-status', 'completed');
        }
        // Update step state
        const step = this.currentPlanSteps.find((s) => s.id === stepId);
        if (step) {
            step.status = status;
        }
        // Update counter with progress
        if (this.currentPlanElement) {
            const completed = this.currentPlanSteps.filter((s) => s.status === 'completed').length;
            const counter = this.currentPlanElement.querySelector('.balancy-plan-counter');
            if (counter) {
                counter.textContent = `${completed}/${this.currentPlanSteps.length}`;
            }
        }
        // Update label styling
        if (labelEl) {
            labelEl.classList.remove('active', 'completed', 'error');
            if (status === 'in_progress')
                labelEl.classList.add('active');
            else if (status === 'completed')
                labelEl.classList.add('completed');
            else if (status === 'error')
                labelEl.classList.add('error');
        }
        // Update typing indicator with current step label
        if (status === 'in_progress' && step) {
            this.setTypingText(step.label + '...');
        }
    }
    /**
     * Collapse the plan checklist after completion.
     */
    collapsePlan() {
        if (!this.currentPlanElement)
            return;
        // Mark any remaining pending steps as completed
        for (const step of this.currentPlanSteps) {
            if (step.status !== 'completed' && step.status !== 'error') {
                step.status = 'completed';
                const stepEl = this.currentPlanElement.querySelector(`[data-step-id="${step.id}"]`);
                if (stepEl) {
                    const iconEl = stepEl.querySelector('.balancy-plan-step-icon');
                    if (iconEl) {
                        iconEl.setAttribute('data-status', 'completed');
                    }
                    const lineEl = stepEl.querySelector('.balancy-plan-step-line');
                    if (lineEl) {
                        lineEl.setAttribute('data-status', 'completed');
                    }
                }
            }
        }
        // Replace counter with "Done" badge
        const header = this.currentPlanElement.querySelector('.balancy-plan-header');
        const counter = this.currentPlanElement.querySelector('.balancy-plan-counter');
        if (counter) {
            counter.remove();
        }
        if (header && !header.querySelector('.balancy-plan-done')) {
            const badge = document.createElement('span');
            badge.className = 'balancy-plan-done';
            badge.textContent = `✓ Done · ${this.currentPlanSteps.length} steps`;
            header.insertBefore(badge, header.querySelector('.balancy-plan-toggle'));
        }
        // Auto-collapse steps after a short delay
        const content = this.currentPlanElement.querySelector('.balancy-plan-content');
        const toggleBtn = this.currentPlanElement.querySelector('.balancy-plan-toggle');
        if (content && toggleBtn) {
            setTimeout(() => {
                content.style.display = 'none';
                toggleBtn.textContent = '▶';
            }, 1500);
        }
        // Reset typing text
        this.setTypingText('Thinking...');
        // Reset state
        this.currentPlanElement = null;
        this.currentPlanSteps = [];
    }
    /**
     * Update the typing indicator text.
     */
    setTypingText(text) {
        const typingIndicator = this.chatPanel?.querySelector('#balancy-typing-indicator');
        if (!typingIndicator)
            return;
        let textSpan = typingIndicator.querySelector('.balancy-typing-text');
        if (!textSpan) {
            // First time: find the existing inline <span> and give it a class
            const existingSpan = typingIndicator.querySelector('span');
            if (existingSpan) {
                existingSpan.className = 'balancy-typing-text';
                textSpan = existingSpan;
            }
        }
        if (textSpan) {
            textSpan.textContent = text;
        }
    }
    /**
     * DEBUG: Show a static plan preview with all possible step states.
     * Call from browser console: document.querySelector('#balancy-ai-root').shadowRoot.__uiManager.showPlanPreview()
     */
    showPlanPreview() {
        const testSteps = [
            { id: 'step_1', label: 'Search documentation' },
            { id: 'step_2', label: 'Load existing entities' },
            { id: 'step_3', label: 'Create Item "Gems"' },
            { id: 'step_4', label: 'Create localization strings' },
            { id: 'step_5', label: 'Load products' },
            { id: 'step_6', label: 'Create StoreItem with price' },
            { id: 'step_7', label: 'Create GameOffer' },
            { id: 'step_8', label: 'Create GameEvent with condition' },
        ];
        this.renderPlanChecklist(testSteps);
        // Simulate different states with delays
        setTimeout(() => this.updatePlanStep('step_1', 'completed'), 200);
        setTimeout(() => this.updatePlanStep('step_2', 'completed'), 400);
        setTimeout(() => this.updatePlanStep('step_3', 'completed'), 600);
        setTimeout(() => this.updatePlanStep('step_4', 'in_progress'), 800);
        setTimeout(() => this.updatePlanStep('step_5', 'error'), 1000);
        // step_6, step_7, step_8 remain pending
    }
    /**
     * Convert tool names to human-readable action descriptions.
     */
    humanizeToolName(toolName, status) {
        const actionMap = {
            createModel: 'Creating entity',
            updateModel: 'Updating entity',
            updateModels: 'Updating entities',
            getModels: 'Loading entities',
            getModelByUnnyId: 'Loading entity',
            getProducts: 'Loading products',
            getTemplates: 'Loading templates',
            getTemplateSchema: 'Loading template schema',
            deleteModel: 'Deleting entity',
            createLocalizationString: 'Creating localization',
            updateLocalizationString: 'Updating localization',
            getLocalizationStrings: 'Loading localizations',
            searchLocalizationStrings: 'Searching localizations',
            getViewData: 'Loading view',
            updateViewData: 'Updating view',
            importHtmlToView: 'Importing HTML to view',
            search_docs: 'Searching documentation',
        };
        const base = actionMap[toolName] || toolName;
        return status === 'completed' ? base + ' ✓' : base + '...';
    }
    destroy() {
        // Clean up analytics (overlay + inline)
        this.hideAnalytics();
        this.unmountAnalyticsInline();
        if (this.creditCountdownTimer) {
            clearInterval(this.creditCountdownTimer);
            this.creditCountdownTimer = null;
        }
        this.floatingIcon?.remove();
        this.chatPanel?.remove();
        // Remove the shadow host from the document entirely
        this.shadowHost?.remove();
        this.floatingIcon = null;
        this.chatPanel = null;
        this.shadowHost = null;
        this.shadowRoot = null;
        this.panelOpen = false;
    }
}
UIManager.LAST_CHANNEL_KEY = 'balancy-last-channel';
UIManager.PANEL_WIDTH_KEY = 'balancy-panel-width';
UIManager.PANEL_POSITION_KEY = 'balancy-panel-position';
UIManager.CONTEXT_KEY = 'balancy-selected-context';
UIManager.CHAT_CONTEXTS = [
    {
        id: 'cms',
        label: 'CMS: Admin Panel',
        color: '#4CAF50',
        description: 'Templates, documents, scripts, offers, events',
    },
    {
        id: 'sdk-unity',
        label: 'SDK: Unity C#',
        color: '#667eea',
        description: 'Unity SDK integration',
    },
    {
        id: 'sdk-ts',
        label: 'SDK: TypeScript',
        color: '#e6a817',
        description: 'TypeScript SDK integration',
    },
];
UIManager.MODEL_KEY = 'balancy-selected-model';
// Welcome message starter questions per chat context
UIManager.STARTER_QUESTIONS = {
    cms: [
        'How do I create a game event?',
        'How do templates and documents work?',
        'How do I set up an offer?',
        'What are Extensions?',
    ],
    'sdk-unity': [
        'How do I initialize the SDK?',
        'How do I access game data at runtime?',
        'How do I work with player profiles?',
        'How do I handle SDK callbacks?',
    ],
    'sdk-ts': [
        'How do I initialize the SDK?',
        'How do I access game data at runtime?',
        'How do I work with player profiles?',
        'How do I handle SDK callbacks?',
    ],
};
// Welcome subtitle per chat context
UIManager.WELCOME_SUBTITLES = {
    cms: 'I can help you manage your game data, set up offers and events, or write extension scripts. What would you like to explore?',
    'sdk-unity': 'I can help you integrate the Balancy SDK into your Unity project, load game data at runtime, and work with player profiles. What would you like to explore?',
    'sdk-ts': 'I can help you integrate the Balancy SDK into your TypeScript project, load game data at runtime, and work with player profiles. What would you like to explore?',
};
